import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, useFormState } from 'react-final-form';
import { Modal, Form as RBForm } from 'react-bootstrap';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import CollapsibleCard from '@spark/ui-components/components/CollapsibleCard';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import SearchSelect from '@spark/ui-components/components/form/SearchSelect';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import TableBadge from '@spark/ui-components/components/TanStackTable/components/TableBadge';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import { notification } from '@spark/ui-components/utils/notification';
import { formatarCNPJ, formatarDataSimples, formatarValorBRL } from '@spark/utils/formatters';
import {
  createDepositoNotaFiscal,
  getDepositoNotaFiscalById,
  updateDepositoNotaFiscal,
  cadastrarDivergenciaDepositoNotaFiscal,
  enviarAnexosDepositoNotaFiscal,
  enviarCanhotoDepositoNotaFiscal,
  finalizarDepositoNotaFiscal,
  finalizarDepositoNotaFiscalEmLote,
  alterarDepositoNotaFiscalEmLote,
  getDepositoNotaFiscalAuditoria,
  getDepositoNotasFiscaisAuditoria,
  getDepositoTabelasPrecoAuditoria,
  exportarPendenciasDeposito,
  obterDocumentacaoDepositoNotas,
  calcularCobrancaPaletizacao,
  registrarCobrancaPaletizacao,
  calcularCobrancaEtiquetagem,
  registrarCobrancaEtiquetagem,
  calcularCobrancaArmazenagem,
  registrarCobrancaArmazenagem,
  calcularCobrancaAvulsa,
  registrarCobrancaAvulsa,
  getNomeClientePorCnpj,
  consultarDepositoNotaFiscalPorChave,
  consultarDepositoCanhotoPorNotaId,
  getDepositoArmazens,
  getDepositoEnderecos,
  getDepositoArmazenagemLocais
} from '@spark/services/deposito';
import { DepositoNotasHeader, DepositoNotasTable, DepositoNotasFilter, DepositoTabelaPrecoSelectionModal, ImportarDepositoNotasModal, DepositoClientesModal, DepositoNotaFiscalAnexosModal, DepositoNotaFiscalCobrancasModal } from './';
import { useDepositoNotasListagem } from '../hooks';
import {
  depositoNotaFiscalFormSections,
  depositoNotaFiscalFormStructure,
  emptyDepositoNotaFiscalForm,
  validateDepositoNotaFiscalForm,
  tipoNotaOptions,
  motivoDevolucaoOptions
} from '../config/depositoNotaFiscalFormConfig';
import { parseQuantidadeOpcional } from '../../utils/parseQuantidadeOpcional';
import {
  buildAnexosPayload,
  buildCanhotoPreviewSrc,
  readFileAsBase64
} from '../utils/depositoNotasFiles';

const QUANTIDADE_OPCIONAL_PAYLOAD_KEYS = new Set(['QuantidadeTotalPaletes', 'QuantidadeTotalEtiquetas']);

const extractValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const parseNumber = (value) => {
  const rawValue = extractValue(value);
  if (rawValue === '' || rawValue === null || rawValue === undefined) return undefined;
  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toDigitsOnly = (value) => (value ? value.replace(/\D/g, '') : undefined);

const sanitizeChaveNFe = (value) => toDigitsOnly(value || '') || '';

const toSelectOption = (value, options) => {
  if (value === null || value === undefined || value === '') return '';
  const stringValue = String(value);
  const found = options.find((option) => String(option.value) === stringValue);
  return found ? { value: found.value, label: found.label } : { value: stringValue, label: stringValue };
};

const toFormDateTime = (value) => {
  if (!value) return '';
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date;
};

const toApiDateTime = (value) => {
  if (!value) return undefined;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    const seconds = String(value.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  const normalized = String(value).trim();
  if (!normalized) return undefined;

  const ptBrDateTime = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
  if (ptBrDateTime) {
    const [, day, month, year, hours, minutes] = ptBrDateTime;
    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
  }

  const isoWithoutTime = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoWithoutTime) {
    return `${normalized}T00:00:00`;
  }

  const isoLocalDateTime = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (isoLocalDateTime) {
    const seconds = isoLocalDateTime[6] || '00';
    return `${isoLocalDateTime[1]}-${isoLocalDateTime[2]}-${isoLocalDateTime[3]}T${isoLocalDateTime[4]}:${isoLocalDateTime[5]}:${seconds}`;
  }

  const isoWithSpace = normalized.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (isoWithSpace) {
    const seconds = isoWithSpace[6] || '00';
    return `${isoWithSpace[1]}-${isoWithSpace[2]}-${isoWithSpace[3]}T${isoWithSpace[4]}:${isoWithSpace[5]}:${seconds}`;
  }

  return normalized;
};

const toInlineDateTimeInputValue = (value) => {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const buildInlineCellKey = (rowId, columnId) => `${rowId}:${columnId}`;

const inlineEditableColumns = {
  dataChegada: {
    label: 'Data Chegada',
    payloadKey: 'DataChegada',
    getOriginalValue: (row) => row?.dataChegadaOriginal || null,
    formatInputValue: (value) => toInlineDateTimeInputValue(value),
    normalizeForCompare: (value) => value || '',
    serialize: (value) => (value ? toApiDateTime(value) : null)
  },
  dataAgendamento: {
    label: 'Data Agendamento',
    payloadKey: 'DataAgendamento',
    getOriginalValue: (row) => row?.dataAgendamentoOriginal || null,
    formatInputValue: (value) => toInlineDateTimeInputValue(value),
    normalizeForCompare: (value) => value || '',
    serialize: (value) => (value ? toApiDateTime(value) : null)
  },
  dataSaida: {
    label: 'Data Saida',
    payloadKey: 'DataSaida',
    getOriginalValue: (row) => row?.dataSaidaOriginal || null,
    formatInputValue: (value) => toInlineDateTimeInputValue(value),
    normalizeForCompare: (value) => value || '',
    serialize: (value) => (value ? toApiDateTime(value) : null)
  },
  placaVeiculo: {
    label: 'Placa Veiculo',
    payloadKey: 'PlacaVeiculo',
    getOriginalValue: (row) => row?.placaVeiculo || '',
    formatInputValue: (value) => value || '',
    normalizeForCompare: (value) => String(value || '').trim(),
    serialize: (value) => {
      const trimmed = String(value || '').trim();
      return trimmed.length > 0 ? trimmed : null;
    }
  },
  modelosReboque: {
    label: 'Modelos Reboque',
    payloadKey: 'ModelosReboque',
    getOriginalValue: (row) => row?.modelosReboque || '',
    formatInputValue: (value) => value || '',
    normalizeForCompare: (value) => String(value || '').trim(),
    serialize: (value) => {
      const trimmed = String(value || '').trim();
      return trimmed.length > 0 ? trimmed : null;
    }
  },
  placasReboque: {
    label: 'Placas Reboque',
    payloadKey: 'PlacasReboque',
    getOriginalValue: (row) => row?.placasReboque || '',
    formatInputValue: (value) => value || '',
    normalizeForCompare: (value) => String(value || '').trim(),
    serialize: (value) => {
      const trimmed = String(value || '').trim();
      return trimmed.length > 0 ? trimmed : null;
    }
  }
};

const buildNumeroPedidoEmbarcador = (notas, tipo) => {
  const buildDateTimeSeed = () => {
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const MM = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const HH = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const SSS = String(now.getMilliseconds()).padStart(3, '0');
    return `${yyyy}${MM}${dd}${HH}${mm}${ss}${SSS}`;
  };

  const normalizePart = (value) => String(value || '').replace(/[^a-zA-Z0-9]/g, '');
  const typePrefix =
    tipo === 'paletizacao' ? 'PAL' : tipo === 'etiquetagem' ? 'ETQ' : tipo === 'armazenagem' ? 'ARM' : 'AVU';
  const seed = buildDateTimeSeed();

  if (!Array.isArray(notas) || notas.length === 0) {
    return `${typePrefix}${seed}`;
  }

  const nota = notas[0] || {};
  const numeroExistente = tipo === 'paletizacao'
    ? nota.numeroPedidoPaletizacao
    : tipo === 'etiquetagem'
      ? nota.numeroPedidoEtiquetagem
      : null;

  if (numeroExistente) return numeroExistente;

  const cnpj = toDigitsOnly(nota.cnpjCliente || '') || '';
  const numeroNF = normalizePart(nota.numeroNF).slice(0, 20);
  const serieNF = normalizePart(nota.serieNF).slice(0, 10);
  const quantidade = notas.length > 1 ? String(notas.length).padStart(2, '0') : '';
  const base = `${typePrefix}${quantidade}${seed}`;

  return base.slice(0, 100);
};


const isNotaCobrada = (nota, tipo) => {
  if (!nota) return false;
  if (tipo === 'paletizacao') {
    return !!nota.cobrancaPaletizacaoRealizada || !!nota.protocoloPedidoTMSPaletizacao;
  }
  if (tipo === 'etiquetagem') {
    return !!nota.cobrancaEtiquetagemRealizada || !!nota.protocoloPedidoTMSEtiquetagem;
  }
  if (tipo === 'armazenagem') {
    return !!nota.cobrancaArmazenagemRealizada;
  }
  if (tipo === 'avulsa') {
    return false;
  }
  return false;
};

const filtrarNotasParaCobranca = (tipo, notas) => {
  const lista = Array.isArray(notas) ? notas : [];
  return lista.filter((nota) => !isNotaCobrada(nota, tipo));
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.message || fallback;

const parseCobrancaErros = (error, fallback) => {
  const message = getApiErrorMessage(error, fallback);
  if (Array.isArray(message)) {
    return message.map((item) => String(item).trim()).filter(Boolean);
  }

  const raw = String(message || '').split(/\r?\n/);
  const parsed = raw.map((item) => item.trim()).filter(Boolean);
  return parsed.length > 0 ? parsed : [fallback];
};

const normalizeFinalizacaoLoteResumo = (response) => {
  const data = response?.data || response || {};
  const itensRaw = Array.isArray(data.itens)
    ? data.itens
    : Array.isArray(data.Itens)
      ? data.Itens
      : [];

  return {
    quantidadeSolicitada: data.quantidadeSolicitada ?? data.QuantidadeSolicitada ?? 0,
    quantidadeProcessada: data.quantidadeProcessada ?? data.QuantidadeProcessada ?? itensRaw.length,
    quantidadeSucesso: data.quantidadeSucesso ?? data.QuantidadeSucesso ?? 0,
    quantidadeErro: data.quantidadeErro ?? data.QuantidadeErro ?? 0,
    itens: itensRaw.map((item) => ({
      id: item.id ?? item.Id,
      numeroNF: item.numeroNF ?? item.NumeroNF ?? '',
      sucesso: item.sucesso ?? item.Sucesso ?? false,
      mensagem: item.mensagem ?? item.Mensagem ?? '',
      jaFinalizada: item.jaFinalizada ?? item.JaFinalizada ?? false
    }))
  };
};

const normalizeAlteracaoLoteResumo = (response) => {
  const data = response?.data || response || {};
  const itensRaw = Array.isArray(data.itens)
    ? data.itens
    : Array.isArray(data.Itens)
      ? data.Itens
      : [];

  return {
    quantidadeSolicitada: data.quantidadeSolicitada ?? data.QuantidadeSolicitada ?? 0,
    quantidadeProcessada: data.quantidadeProcessada ?? data.QuantidadeProcessada ?? itensRaw.length,
    quantidadeSucesso: data.quantidadeSucesso ?? data.QuantidadeSucesso ?? 0,
    quantidadeErro: data.quantidadeErro ?? data.QuantidadeErro ?? 0,
    itens: itensRaw.map((item) => ({
      id: item.id ?? item.Id,
      numeroNF: item.numeroNF ?? item.NumeroNF ?? '',
      sucesso: item.sucesso ?? item.Sucesso ?? false,
      mensagem: item.mensagem ?? item.Mensagem ?? ''
    }))
  };
};

const emptyAlteracaoLoteForm = {
  aplicarFinalizado: false,
  finalizado: false,
  aplicarExisteDivergencia: false,
  existeDivergencia: false,
  aplicarDataSaida: false,
  dataSaida: '',
  aplicarDataAgendamento: false,
  dataAgendamento: '',
  aplicarDataChegada: false,
  dataChegada: '',
  aplicarDataLeitura: false,
  dataLeitura: '',
  aplicarDataConferencia: false,
  dataConferencia: '',
  aplicarDataArmazenagem: false,
  dataArmazenagem: '',
  aplicarTipoOperacao: false,
  tipoOperacao: '',
  aplicarObservacoes: false,
  observacoes: '',
  aplicarCobrancaPaletizacaoRealizada: false,
  cobrancaPaletizacaoRealizada: false,
  aplicarCobrancaEtiquetagemRealizada: false,
  cobrancaEtiquetagemRealizada: false,
  aplicarCobrancaArmazenagemRealizada: false,
  cobrancaArmazenagemRealizada: false
};

const emptyArmazenamentoLoteForm = {
  idDepositoArmazem: '',
  idDepositoEndereco: '',
  idDepositoArmazenagemLocal: ''
};

const parsePaginacaoResponse = (response) => {
  const data = response?.data || response || {};
  return {
    registros: Array.isArray(data.registros)
      ? data.registros
      : Array.isArray(data.Registros)
        ? data.Registros
        : [],
    totalRegistros: data.totalRegistros ?? data.TotalRegistros ?? 0
  };
};

const normalizeEnderecoSegment = (value) => (value === null || value === undefined ? '' : String(value).trim());

const formatEnderecoDeposito = (endereco) => {
  if (!endereco) return '-';

  const codigoArmazem = normalizeEnderecoSegment(endereco.armazem?.codigo);
  const bloco = normalizeEnderecoSegment(endereco.bloco);
  const rua = endereco.rua ?? '';
  const nivel = endereco.nivel ?? '';
  const posicao = endereco.posicao ?? '';

  if (!codigoArmazem && !bloco && rua === '' && nivel === '' && posicao === '') {
    return '-';
  }

  return `${codigoArmazem}.${bloco}.${rua}.${nivel}.${posicao}`;
};

const normalizeAuditoriaAcao = (value) => {
  const rawValue = value ?? '';
  if (rawValue === 1 || rawValue === '1' || rawValue === 'Criacao') return 'Criacao';
  if (rawValue === 2 || rawValue === '2' || rawValue === 'Atualizacao') return 'Atualizacao';
  if (rawValue === 3 || rawValue === '3' || rawValue === 'Exclusao') return 'Exclusao';
  return String(rawValue || '-');
};

const formatarDataHoraAuditoria = (value) => {
  if (!value) return '-';
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) {
    return String(value);
  }

  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const normalizeAuditoriaResponse = (response) => {
  const data = response?.data || response || {};
  const registrosRaw = Array.isArray(data.registros)
    ? data.registros
    : Array.isArray(data.Registros)
      ? data.Registros
      : [];

  return {
    totalRegistros: data.totalRegistros ?? data.TotalRegistros ?? registrosRaw.length,
    paginaAtual: data.paginaAtual ?? data.PaginaAtual ?? 1,
    quantidadePorPagina: data.quantidadePorPagina ?? data.QuantidadePorPagina ?? 10,
    registros: registrosRaw.map((item, index) => ({
      id: item.id ?? item.Id ?? `${index}-${item.campo || item.Campo || 'audit'}`,
      idDepositoNotaFiscal: item.idDepositoNotaFiscal ?? item.IdDepositoNotaFiscal ?? null,
      numeroNF: item.numeroNF ?? item.NumeroNF ?? '',
      cnpjCliente: item.cnpjCliente ?? item.CNPJCliente ?? '',
      dataHora: item.dataHora ?? item.DataHora ?? '',
      usuario: item.usuario ?? item.Usuario ?? '-',
      acao: normalizeAuditoriaAcao(item.acao ?? item.Acao),
      campo: item.campo ?? item.Campo ?? '-',
      valorAntigo: item.valorAntigo ?? item.ValorAntigo ?? null,
      valorNovo: item.valorNovo ?? item.ValorNovo ?? null
    }))
  };
};

const normalizeAuditoriaTabelaPrecoResponse = (response) => {
  const data = response?.data || response || {};
  const registrosRaw = Array.isArray(data.registros)
    ? data.registros
    : Array.isArray(data.Registros)
      ? data.Registros
      : [];

  return {
    totalRegistros: data.totalRegistros ?? data.TotalRegistros ?? registrosRaw.length,
    paginaAtual: data.paginaAtual ?? data.PaginaAtual ?? 1,
    quantidadePorPagina: data.quantidadePorPagina ?? data.QuantidadePorPagina ?? 10,
    registros: registrosRaw.map((item, index) => ({
      id: item.id ?? item.Id ?? `${index}-${item.campo || item.Campo || 'audit-tabela'}`,
      idDepositoTabelaPreco: item.idDepositoTabelaPreco ?? item.IdDepositoTabelaPreco ?? null,
      nomeTabela: item.nomeTabela ?? item.NomeTabela ?? '',
      responsavel: item.responsavel ?? item.Responsavel ?? '',
      cnpjCliente: item.cnpjCliente ?? item.CNPJCliente ?? '',
      nomeCliente: item.nomeCliente ?? item.NomeCliente ?? '',
      dataHora: item.dataHora ?? item.DataHora ?? '',
      usuario: item.usuario ?? item.Usuario ?? '-',
      acao: normalizeAuditoriaAcao(item.acao ?? item.Acao),
      campo: item.campo ?? item.Campo ?? '-',
      valorAntigo: item.valorAntigo ?? item.ValorAntigo ?? null,
      valorNovo: item.valorNovo ?? item.ValorNovo ?? null
    }))
  };
};

const auditoriaGeralFilterConfig = [
  {
    id: 'NumeroNF',
    type: 'text',
    label: 'Numero NF',
    placeholder: 'Digite o numero da NF'
  },
  {
    id: 'CNPJCliente',
    type: 'text',
    label: 'CNPJ Cliente',
    placeholder: 'Digite o CNPJ'
  },
  {
    id: 'DataInicial',
    type: 'date',
    label: 'Data Inicial'
  },
  {
    id: 'DataFinal',
    type: 'date',
    label: 'Data Final'
  },
  {
    id: 'Acao',
    type: 'select',
    label: 'Acao',
    placeholder: 'Selecione...',
    options: [
      { value: 'Criacao', label: 'Criacao' },
      { value: 'Atualizacao', label: 'Atualizacao' },
      { value: 'Exclusao', label: 'Exclusao' }
    ]
  },
  {
    id: 'Usuario',
    type: 'text',
    label: 'Usuario',
    placeholder: 'Digite o usuario'
  }
];

const auditoriaTabelaPrecoFilterConfig = [
  {
    id: 'CNPJCliente',
    type: 'text',
    label: 'CNPJ Cliente',
    placeholder: 'Digite o CNPJ'
  },
  {
    id: 'NomeCliente',
    type: 'text',
    label: 'Nome Cliente',
    placeholder: 'Digite o nome do cliente'
  },
  {
    id: 'Nome',
    type: 'text',
    label: 'Nome',
    placeholder: 'Digite o nome da tabela'
  },
  {
    id: 'DataInicial',
    type: 'date',
    label: 'Data Inicial'
  },
  {
    id: 'DataFinal',
    type: 'date',
    label: 'Data Final'
  },
  {
    id: 'Acao',
    type: 'select',
    label: 'Acao',
    placeholder: 'Selecione...',
    options: [
      { value: 'Criacao', label: 'Criacao' },
      { value: 'Atualizacao', label: 'Atualizacao' },
      { value: 'Exclusao', label: 'Exclusao' }
    ]
  },
  {
    id: 'Usuario',
    type: 'text',
    label: 'Usuario',
    placeholder: 'Digite o usuario'
  },
  {
    id: 'Responsavel',
    type: 'text',
    label: 'Responsavel',
    placeholder: 'Digite o responsavel'
  }
];

const buildObservacoesCobranca = (calculo) => {
  if (!calculo) return '';
  return String(calculo.observacoesNotas || '').trim();
};

const colunaObsTabelaPreco = {
  id: 'obsTabelaPreco',
  header: 'Obs. Tabela Preco',
  accessorKey: 'obsTabelaPreco',
  size: 220,
  enableSorting: false,
  cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={28} />
};

const mapApiToFormValues = (data) => ({
  id: data?.id ?? '',
  chaveNFe: data?.chaveNFe || '',
  numeroNF: data?.numeroNF || '',
  serieNF: data?.serieNF || '',
  tipoNota: toSelectOption(data?.tipoNota ?? 1, tipoNotaOptions),
  descricaoDevolucao: data?.descricaoDevolucao || '',
  motivoDevolucao: toSelectOption(data?.motivoDevolucao, motivoDevolucaoOptions),
  numeroPedidoPaletizacao: data?.numeroPedidoPaletizacao || '',
  numeroPedidoEtiquetagem: data?.numeroPedidoEtiquetagem || '',
  dataEmissaoPaletizacao: data?.dataEmissaoPaletizacao ? data.dataEmissaoPaletizacao : '',
  dataEmissaoEtiquetagem: data?.dataEmissaoEtiquetagem ? data.dataEmissaoEtiquetagem : '',
  cnpjCliente: formatarCNPJ(data?.cnpjCliente || ''),
  nomeCliente: data?.nomeCliente || '',
  tipoOperacao: data?.tipoOperacao || '',
  nomeDestinatario: data?.nomeDestinatario || '',
  nomeRemetente: data?.nomeRemetente || '',
  nomeUsuarioLeitura: data?.nomeUsuarioLeitura || '',
  nomeResponsavelComercial: data?.nomeResponsavelComercial || '',
  nomeResponsavelLancamento: data?.nomeResponsavelLancamento || '',
  nomeResponsavelFechamento: data?.nomeResponsavelFechamento || '',
  dataChegada: data?.dataChegada ? toFormDateTime(data.dataChegada) : '',
  dataLeitura: data?.dataLeitura ? toFormDateTime(data.dataLeitura) : '',
  dataConferencia: data?.dataConferencia ? toFormDateTime(data.dataConferencia) : '',
  dataArmazenagem: data?.dataArmazenagem ? toFormDateTime(data.dataArmazenagem) : '',
  dataAgendamento: data?.dataAgendamento ? toFormDateTime(data.dataAgendamento) : '',
  dataSaida: data?.dataSaida ? toFormDateTime(data.dataSaida) : '',
  dataEnvioCanhoto: data?.dataEnvioCanhoto ? toFormDateTime(data.dataEnvioCanhoto) : '',
  quantidadeTotalCaixas: data?.quantidadeTotalCaixas ?? '',
  quantidadeTotalPaletes: data?.quantidadeTotalPaletes ?? '',
  quantidadeTotalEtiquetas: data?.quantidadeTotalEtiquetas ?? '',
  quantidadePaletesCobranca: data?.quantidadePaletesCobranca ?? '',
  quantidadeEtiquetasCobranca: data?.quantidadeEtiquetasCobranca ?? '',
  idDepositoTabelaPreco: data?.idDepositoTabelaPreco
    ? {
        value: String(data.idDepositoTabelaPreco),
        label: data.tabelaPreco?.nomeTabelaPrecoDeposito || `Tabela ${data.idDepositoTabelaPreco}`
      }
    : '',
  valorUnPallet: data?.valorUnPallet ?? '',
  valorUnEtiqueta: data?.valorUnEtiqueta ?? '',
  valorUnArmazenagemPallet: data?.valorUnArmazenagemPallet ?? '',
  freetime: data?.freetime ?? '',
  valorTotalPaletizacao: data?.valorTotalPaletizacao ?? '',
  valorTotalEtiquetagem: data?.valorTotalEtiquetagem ?? '',
  valorTotalArmazenagemPallet: data?.valorTotalArmazenagemPallet ?? '',
  valorTotalEmitidoPaletizacao: data?.valorTotalEmitidoPaletizacao ?? '',
  valorTotalEmitidoEtiquetagem: data?.valorTotalEmitidoEtiquetagem ?? '',
  usuarioEmissaoPaletizacao: data?.usuarioEmissaoPaletizacao || '',
  usuarioEmissaoEtiquetagem: data?.usuarioEmissaoEtiquetagem || '',
  protocoloPedidoTMSPaletizacao: data?.protocoloPedidoTMSPaletizacao || '',
  protocoloPedidoTMSEtiquetagem: data?.protocoloPedidoTMSEtiquetagem || '',
  urlImagemCanhoto: data?.urlImagemCanhoto || '',
  nomeUsuarioEnvioCanhoto: data?.nomeUsuarioEnvioCanhoto || '',
  mudancaTabelaPrecoManual: data?.mudancaTabelaPrecoManual ?? false,
  cobrancaPaletizacaoRealizada: data?.cobrancaPaletizacaoRealizada ?? false,
  cobrancaEtiquetagemRealizada: data?.cobrancaEtiquetagemRealizada ?? false,
  cobrancaArmazenagemRealizada: data?.cobrancaArmazenagemRealizada ?? false,
  existeDivergencia: data?.existeDivergencia ?? false,
  finalizado: data?.finalizado ?? false,
  usuarioFinalizacao: data?.usuarioFinalizacao || '',
  descricaoDivergencia: data?.descricaoDivergencia || '',
  observacoes: data?.observacoes || ''
});

const mapRowToFormValues = (row) => ({
  id: row?.id ?? '',
  chaveNFe: row?.chaveNFe || '',
  numeroNF: row?.numeroNF || '',
  serieNF: row?.serieNF || '',
  tipoNota: toSelectOption(row?.tipoNota ?? 1, tipoNotaOptions),
  descricaoDevolucao: row?.descricaoDevolucao || '',
  motivoDevolucao: toSelectOption(row?.motivoDevolucao, motivoDevolucaoOptions),
  numeroPedidoPaletizacao: row?.numeroPedidoPaletizacao || '',
  numeroPedidoEtiquetagem: row?.numeroPedidoEtiquetagem || '',
  dataEmissaoPaletizacao: row?.dataEmissaoPaletizacao || '',
  dataEmissaoEtiquetagem: row?.dataEmissaoEtiquetagem || '',
  cnpjCliente: formatarCNPJ(row?.cnpjCliente || ''),
  nomeCliente: row?.nomeCliente || '',
  tipoOperacao: row?.tipoOperacao || '',
  nomeDestinatario: row?.nomeDestinatario || '',
  nomeRemetente: row?.nomeRemetente || '',
  nomeUsuarioLeitura: row?.nomeUsuarioLeitura || '',
  nomeResponsavelComercial: row?.nomeResponsavelComercial || '',
  nomeResponsavelLancamento: row?.nomeResponsavelLancamento || '',
  nomeResponsavelFechamento: row?.nomeResponsavelFechamento || '',
  dataChegada: row?.dataChegadaOriginal ? toFormDateTime(row.dataChegadaOriginal) : '',
  dataLeitura: row?.dataLeituraOriginal ? toFormDateTime(row.dataLeituraOriginal) : '',
  dataConferencia: row?.dataConferenciaOriginal ? toFormDateTime(row.dataConferenciaOriginal) : '',
  dataArmazenagem: row?.dataArmazenagemOriginal ? toFormDateTime(row.dataArmazenagemOriginal) : '',
  dataAgendamento: row?.dataAgendamentoOriginal ? toFormDateTime(row.dataAgendamentoOriginal) : '',
  dataSaida: row?.dataSaidaOriginal ? toFormDateTime(row.dataSaidaOriginal) : '',
  dataEnvioCanhoto: row?.dataEnvioCanhotoOriginal ? toFormDateTime(row.dataEnvioCanhotoOriginal) : '',
  quantidadeTotalCaixas: row?.quantidadeTotalCaixas ?? '',
  quantidadeTotalPaletes: row?.quantidadeTotalPaletes ?? '',
  quantidadeTotalEtiquetas: row?.quantidadeTotalEtiquetas ?? '',
  quantidadePaletesCobranca: row?.quantidadePaletesCobranca ?? '',
  quantidadeEtiquetasCobranca: row?.quantidadeEtiquetasCobranca ?? '',
  idDepositoTabelaPreco: row?.idDepositoTabelaPreco
    ? {
        value: String(row.idDepositoTabelaPreco),
        label: row.tabelaPrecoNome || row.tabelaPreco || `Tabela ${row.idDepositoTabelaPreco}`
      }
    : '',
  valorUnPallet: row?.valorUnPallet ?? '',
  valorUnEtiqueta: row?.valorUnEtiqueta ?? '',
  valorUnArmazenagemPallet: row?.valorUnArmazenagemPallet ?? '',
  freetime: row?.freetime ?? '',
  valorTotalPaletizacao: row?.valorTotalPaletizacao ?? '',
  valorTotalEtiquetagem: row?.valorTotalEtiquetagem ?? '',
  valorTotalArmazenagemPallet: row?.valorTotalArmazenagemPallet ?? '',
  valorTotalEmitidoPaletizacao: row?.valorTotalEmitidoPaletizacao ?? '',
  valorTotalEmitidoEtiquetagem: row?.valorTotalEmitidoEtiquetagem ?? '',
  usuarioEmissaoPaletizacao: row?.usuarioEmissaoPaletizacao || '',
  usuarioEmissaoEtiquetagem: row?.usuarioEmissaoEtiquetagem || '',
  protocoloPedidoTMSPaletizacao: row?.protocoloPedidoTMSPaletizacao || '',
  protocoloPedidoTMSEtiquetagem: row?.protocoloPedidoTMSEtiquetagem || '',
  urlImagemCanhoto: row?.urlImagemCanhoto || '',
  nomeUsuarioEnvioCanhoto: row?.nomeUsuarioEnvioCanhoto || '',
  mudancaTabelaPrecoManual: row?.mudancaTabelaPrecoManual ?? false,
  cobrancaPaletizacaoRealizada: row?.cobrancaPaletizacaoRealizada ?? false,
  cobrancaEtiquetagemRealizada: row?.cobrancaEtiquetagemRealizada ?? false,
  cobrancaArmazenagemRealizada: row?.cobrancaArmazenagemRealizada ?? false,
  existeDivergencia: row?.existeDivergencia ?? false,
  finalizado: row?.finalizado ?? false,
  usuarioFinalizacao: row?.usuarioFinalizacao || '',
  descricaoDivergencia: row?.descricaoDivergencia || '',
  observacoes: row?.observacoes || ''
});

const buildPayload = (values, includeEmpty = false) => {
  const safeNumber = (value) => {
    const parsed = parseNumber(value);
    return parsed === undefined ? (includeEmpty ? null : undefined) : parsed;
  };
  const safeQuantidadeOpcional = (value) => {
    const parsed = parseQuantidadeOpcional(extractValue(value));
    return parsed === undefined ? null : parsed;
  };
  const safeTipoNota = (value) => {
    const rawValue = extractValue(value);
    if (rawValue === undefined || rawValue === null || rawValue === '') return includeEmpty ? null : undefined;
    const parsed = parseInt(String(rawValue), 10);
    return Number.isNaN(parsed) ? (includeEmpty ? null : undefined) : parsed;
  };
  const safeMotivoDevolucao = (value) => {
    const rawValue = extractValue(value);
    if (rawValue === undefined || rawValue === null || rawValue === '') return includeEmpty ? null : undefined;
    const parsed = parseInt(String(rawValue), 10);
    return Number.isNaN(parsed) ? (includeEmpty ? null : undefined) : parsed;
  };
  const safeDate = (value) => {
    if (!value) return includeEmpty ? null : undefined;
    return toApiDateTime(value);
  };
  const safeText = (value) => {
    const normalized = normalizeText(value);
    return normalized === null ? (includeEmpty ? null : undefined) : normalized;
  };

  const payload = {
    NumeroNF: safeText(values.numeroNF),
    SerieNF: safeText(values.serieNF),
    ChaveNFe: includeEmpty ? (toDigitsOnly(values.chaveNFe) || null) : toDigitsOnly(values.chaveNFe),
    TipoNota: safeTipoNota(values.tipoNota),
    DescricaoDevolucao: safeText(values.descricaoDevolucao),
    MotivoDevolucao: safeMotivoDevolucao(values.motivoDevolucao),
    CNPJCliente: includeEmpty ? (toDigitsOnly(values.cnpjCliente) || null) : toDigitsOnly(values.cnpjCliente),
    NomeCliente: safeText(values.nomeCliente),
    TipoOperacao: safeText(values.tipoOperacao),
    NomeDestinatario: safeText(values.nomeDestinatario),
    NomeRemetente: safeText(values.nomeRemetente),
    NomeUsuarioLeitura: safeText(values.nomeUsuarioLeitura),
    NomeResponsavelComercial: safeText(values.nomeResponsavelComercial),
    NomeResponsavelLancamento: safeText(values.nomeResponsavelLancamento),
    NomeResponsavelFechamento: safeText(values.nomeResponsavelFechamento),
    DataChegada: safeDate(values.dataChegada),
    DataLeitura: safeDate(values.dataLeitura),
    DataConferencia: safeDate(values.dataConferencia),
    DataArmazenagem: safeDate(values.dataArmazenagem),
    DataAgendamento: safeDate(values.dataAgendamento),
    DataSaida: safeDate(values.dataSaida),
    DataEnvioCanhoto: safeDate(values.dataEnvioCanhoto),
    QuantidadeTotalCaixas: safeNumber(values.quantidadeTotalCaixas),
    QuantidadeTotalPaletes: safeQuantidadeOpcional(values.quantidadeTotalPaletes),
    QuantidadeTotalEtiquetas: safeQuantidadeOpcional(values.quantidadeTotalEtiquetas),
    QuantidadePaletesCobranca: safeNumber(values.quantidadePaletesCobranca),
    QuantidadeEtiquetasCobranca: safeNumber(values.quantidadeEtiquetasCobranca),
    IdDepositoTabelaPreco: safeNumber(values.idDepositoTabelaPreco),
    ValorUnPallet: safeNumber(values.valorUnPallet),
    ValorUnEtiqueta: safeNumber(values.valorUnEtiqueta),
    ValorUnArmazenagemPallet: safeNumber(values.valorUnArmazenagemPallet),
    Freetime: safeNumber(values.freetime),
    ValorTotalPaletizacao: safeNumber(values.valorTotalPaletizacao),
    ValorTotalEtiquetagem: safeNumber(values.valorTotalEtiquetagem),
    ValorTotalArmazenagemPallet: safeNumber(values.valorTotalArmazenagemPallet),
    ProtocoloPedidoTMSPaletizacao: safeText(values.protocoloPedidoTMSPaletizacao),
    ProtocoloPedidoTMSEtiquetagem: safeText(values.protocoloPedidoTMSEtiquetagem),
    Observacoes: safeText(values.observacoes),
    MudancaTabelaPrecoManual: values.mudancaTabelaPrecoManual,
    UrlImagemCanhoto: safeText(values.urlImagemCanhoto),
    NomeUsuarioEnvioCanhoto: safeText(values.nomeUsuarioEnvioCanhoto),
    CobrancaPaletizacaoRealizada: values.cobrancaPaletizacaoRealizada,
    CobrancaEtiquetagemRealizada: values.cobrancaEtiquetagemRealizada,
    CobrancaArmazenagemRealizada: values.cobrancaArmazenagemRealizada,
    ExisteDivergencia: values.existeDivergencia,
    Finalizado: values.finalizado,
    DescricaoDivergencia: safeText(values.descricaoDivergencia)
  };

  if (includeEmpty) {
    return payload;
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => {
      if (QUANTIDADE_OPCIONAL_PAYLOAD_KEYS.has(key) && value === null) {
        return true;
      }

      return value !== undefined && value !== null && value !== '';
    })
  );
};

const buildUpdatePayload = (initialValues, currentValues) => {
  const initialPayload = buildPayload(initialValues, true);
  const currentPayload = buildPayload(currentValues, true);
  const allKeys = new Set([...Object.keys(initialPayload), ...Object.keys(currentPayload)]);

  return Array.from(allKeys).reduce((acc, key) => {
    if (initialPayload[key] !== currentPayload[key]) {
      acc[key] = currentPayload[key];
    }

    return acc;
  }, {});
};

const renderBoolBadge = (value) => {
  if (value === true) {
    return <TableBadge label="Sim" variant="success" />;
  }

  if (value === false) {
    return <TableBadge label="Nao" variant="danger" />;
  }

  return <TableBadge label="N/A" variant="default" />;
};

const buildCobrancaFormStructure = (cobrancaTipo) => {
  const primeiraLinha = [
    {
      name: 'valorTotal',
      label: 'Valor Total',
      component: 'CurrencyInput',
      placeholder: 'Informe o valor total',
      colProps: { md: cobrancaTipo === 'avulsa' ? 4 : cobrancaTipo === 'armazenagem' ? 4 : 6 }
    }
  ];

  if (cobrancaTipo === 'avulsa') {
    primeiraLinha.push(
      {
        name: 'tipo',
        label: 'Tipo',
        component: 'FormField',
        type: 'text',
        placeholder: 'Informe o tipo da cobranca',
        colProps: { md: 4 }
      },
      {
        name: 'numeroPedidoEmbarcador',
        label: 'Numero Pedido Embarcador',
        component: 'FormField',
        type: 'text',
        placeholder: 'Informe o numero do pedido',
        colProps: { md: 4 },
        readOnly: true,
        disabled: true
      }
    );
  } else if (cobrancaTipo === 'armazenagem') {
    primeiraLinha.push(
      {
        name: 'numeroPedidoEmbarcador',
        label: 'Numero Pedido Embarcador',
        component: 'FormField',
        type: 'text',
        placeholder: 'Informe o numero do pedido',
        colProps: { md: 4 },
        readOnly: true,
        disabled: true
      },
      {
        name: 'dataCobranca',
        label: 'Data cobranca',
        placeholder: 'Selecione data e hora da cobranca',
        component: 'DatePickers',
        type: 'datetime',
        colProps: { md: 4 }
      }
    );
  } else {
    primeiraLinha.push({
      name: 'numeroPedidoEmbarcador',
      label: 'Numero Pedido Embarcador',
      component: 'FormField',
      type: 'text',
      placeholder: 'Informe o numero do pedido',
      colProps: { md: 6 },
      readOnly: true,
      disabled: true
    });
  }

  return [
    primeiraLinha,
    [
      {
        name: 'observacoes',
        label: 'Observacoes',
        component: 'Textarea',
        type: 'textarea',
        placeholder: 'Informe as observacoes da cobranca',
        colProps: { md: 12 },
        rows: 3
      }
    ]
  ];
};
const cobrancaFormSections = [{ title: '', rows: [0, 1] }];

const CobrancaArmazenagemDataObserver = ({ enabled, onChange }) => {
  const { values } = useFormState({ subscription: { values: true } });
  const previousValueRef = useRef(toApiDateTime(values?.dataCobranca) || '');

  useEffect(() => {
    if (!enabled) return;

    const nextValue = toApiDateTime(values?.dataCobranca) || '';
    if (nextValue === previousValueRef.current) return;

    previousValueRef.current = nextValue;
    onChange(values?.dataCobranca || null);
  }, [enabled, onChange, values]);

  return null;
};

const getCobrancaTipoLabel = (tipo) => {
  if (tipo === 'paletizacao') return 'Paletizacao';
  if (tipo === 'etiquetagem') return 'Etiquetagem';
  if (tipo === 'armazenagem') return 'Armazenagem';
  if (tipo === 'avulsa') return 'Avulsa';
  return 'Cobranca';
};

const DepositoNotasCard = () => {
  const [formInitialValues, setFormInitialValues] = useState(emptyDepositoNotaFiscalForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [inlineEditingCell, setInlineEditingCell] = useState(null);
  const [inlineEditingValue, setInlineEditingValue] = useState('');
  const [inlineSavingCellKey, setInlineSavingCellKey] = useState(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(true);
  const [showCobrancaModal, setShowCobrancaModal] = useState(false);
  const [showCobrancaErroModal, setShowCobrancaErroModal] = useState(false);
  const [cobrancaTipo, setCobrancaTipo] = useState('paletizacao');
  const [cobrancaCalculo, setCobrancaCalculo] = useState(null);
  const [cobrancaFormInitialValues, setCobrancaFormInitialValues] = useState({
    valorTotal: '',
    numeroPedidoEmbarcador: '',
    observacoes: '',
    tipo: '',
    dataCobranca: ''
  });
  const [cobrancaLoading, setCobrancaLoading] = useState(false);
  const [cobrancaRecalculando, setCobrancaRecalculando] = useState(false);
  const [cobrancaGerando, setCobrancaGerando] = useState(false);
  const [cobrancaErros, setCobrancaErros] = useState([]);
  const [loadingAcao, setLoadingAcao] = useState(null);
  const [showDivergenciaModal, setShowDivergenciaModal] = useState(false);
  const [divergenciaMensagem, setDivergenciaMensagem] = useState('');
  const [divergenciaArquivos, setDivergenciaArquivos] = useState([]);
  const [divergenciaSalvando, setDivergenciaSalvando] = useState(false);
  const [showAnexoModal, setShowAnexoModal] = useState(false);
  const [showAnexosListModal, setShowAnexosListModal] = useState(false);
  const [showCobrancasListModal, setShowCobrancasListModal] = useState(false);
  const [anexosReloadTrigger, setAnexosReloadTrigger] = useState(0);
  const [anexoArquivos, setAnexoArquivos] = useState([]);
  const [anexoSalvando, setAnexoSalvando] = useState(false);
  const [showCanhotoModal, setShowCanhotoModal] = useState(false);
  const [canhotoArquivo, setCanhotoArquivo] = useState(null);
  const [canhotoSerieNF, setCanhotoSerieNF] = useState('');
  const [canhotoSalvando, setCanhotoSalvando] = useState(false);
  const [showVisualizarCanhotoModal, setShowVisualizarCanhotoModal] = useState(false);
  const [canhotoPreviewSrc, setCanhotoPreviewSrc] = useState('');
  const [canhotoPreviewTitulo, setCanhotoPreviewTitulo] = useState('');
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [finalizarSalvando, setFinalizarSalvando] = useState(false);
  const [showAuditoriaModal, setShowAuditoriaModal] = useState(false);
  const [auditoriaLoading, setAuditoriaLoading] = useState(false);
  const [auditoriaData, setAuditoriaData] = useState([]);
  const [auditoriaTotal, setAuditoriaTotal] = useState(0);
  const [auditoriaPagination, setAuditoriaPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [showAuditoriaGeralModal, setShowAuditoriaGeralModal] = useState(false);
  const [auditoriaGeralLoading, setAuditoriaGeralLoading] = useState(false);
  const [auditoriaGeralData, setAuditoriaGeralData] = useState([]);
  const [auditoriaGeralTotal, setAuditoriaGeralTotal] = useState(0);
  const [auditoriaGeralFiltros, setAuditoriaGeralFiltros] = useState({});
  const [auditoriaGeralPagination, setAuditoriaGeralPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [showAuditoriaTabelaPrecoModal, setShowAuditoriaTabelaPrecoModal] = useState(false);
  const [auditoriaTabelaPrecoLoading, setAuditoriaTabelaPrecoLoading] = useState(false);
  const [auditoriaTabelaPrecoData, setAuditoriaTabelaPrecoData] = useState([]);
  const [auditoriaTabelaPrecoTotal, setAuditoriaTabelaPrecoTotal] = useState(0);
  const [auditoriaTabelaPrecoFiltros, setAuditoriaTabelaPrecoFiltros] = useState({});
  const [auditoriaTabelaPrecoPagination, setAuditoriaTabelaPrecoPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [showFinalizarLoteModal, setShowFinalizarLoteModal] = useState(false);
  const [finalizarLoteSalvando, setFinalizarLoteSalvando] = useState(false);
  const [showFinalizarLoteResumoModal, setShowFinalizarLoteResumoModal] = useState(false);
  const [finalizacaoLoteResumo, setFinalizacaoLoteResumo] = useState(null);
  const [showAlteracaoLoteModal, setShowAlteracaoLoteModal] = useState(false);
  const [alteracaoLoteSalvando, setAlteracaoLoteSalvando] = useState(false);
  const [showAlteracaoLoteResumoModal, setShowAlteracaoLoteResumoModal] = useState(false);
  const [alteracaoLoteResumo, setAlteracaoLoteResumo] = useState(null);
  const [alteracaoLoteForm, setAlteracaoLoteForm] = useState(emptyAlteracaoLoteForm);
  const [showArmazenamentoLoteModal, setShowArmazenamentoLoteModal] = useState(false);
  const [armazenamentoLoteSalvando, setArmazenamentoLoteSalvando] = useState(false);
  const [showArmazenamentoLoteResumoModal, setShowArmazenamentoLoteResumoModal] = useState(false);
  const [armazenamentoLoteResumo, setArmazenamentoLoteResumo] = useState(null);
  const [armazenamentoLoteForm, setArmazenamentoLoteForm] = useState(emptyArmazenamentoLoteForm);
  const [armazensDeposito, setArmazensDeposito] = useState([]);
  const [enderecosDeposito, setEnderecosDeposito] = useState([]);
  const [armazenagemLocais, setArmazenagemLocais] = useState([]);
  const [loadingArmazenamentoLookup, setLoadingArmazenamentoLookup] = useState(false);
  const [documentacaoLoading, setDocumentacaoLoading] = useState(false);
  const [exportandoPendencias, setExportandoPendencias] = useState(false);
  const [showImportarModal, setShowImportarModal] = useState(false);
  const [showClientesModal, setShowClientesModal] = useState(false);
  const [selectedNotas, setSelectedNotas] = useState([]);
  const [bulkCobrancaLoading, setBulkCobrancaLoading] = useState(null);
  const [cobrancaNotasSelecionadas, setCobrancaNotasSelecionadas] = useState([]);
  const formRef = useRef(null);
  const formularioRef = useRef(null);
  const cobrancaFormRef = useRef(null);
  const nomeClienteLookupRef = useRef('');
  const chaveNfeLookupRef = useRef('');
  const [consultandoChaveNfe, setConsultandoChaveNfe] = useState(false);
  const cobrancaFormStructure = useMemo(() => buildCobrancaFormStructure(cobrancaTipo), [cobrancaTipo]);
  const modalComponents = {
    idDepositoTabelaPreco: DepositoTabelaPrecoSelectionModal
  };
  const handleTabelaPrecoSelect = useCallback((tabela) => {
    if (!formRef.current || !tabela) return;

    formRef.current.change('valorUnPallet', tabela.valorUnPallet ?? '');
    formRef.current.change('valorUnEtiqueta', tabela.valorUnEtiqueta ?? '');
    formRef.current.change('valorUnArmazenagemPallet', tabela.valorUnArmazenagemPallet ?? '');
    formRef.current.change('freetime', tabela.freetime ?? '');
  }, []);
  const modalProps = useMemo(
    () => ({
      idDepositoTabelaPreco: {
        onSelectComplete: handleTabelaPrecoSelect
      }
    }),
    [handleTabelaPrecoSelect]
  );

  const carregarArmazenamentoLookups = useCallback(async () => {
    setLoadingArmazenamentoLookup(true);
    try {
      const [armazensResponse, enderecosResponse, locaisResponse] = await Promise.all([
        getDepositoArmazens({ PaginaAtual: 1, QuantidadePorPagina: 200 }),
        getDepositoEnderecos({ PaginaAtual: 1, QuantidadePorPagina: 1000 }),
        getDepositoArmazenagemLocais()
      ]);

      setArmazensDeposito(parsePaginacaoResponse(armazensResponse).registros);
      setEnderecosDeposito(parsePaginacaoResponse(enderecosResponse).registros);

      const locais = Array.isArray(locaisResponse?.data)
        ? locaisResponse.data
        : Array.isArray(locaisResponse)
          ? locaisResponse
          : [];

      setArmazenagemLocais(locais);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar dados de armazenamento'));
      throw error;
    } finally {
      setLoadingArmazenamentoLookup(false);
    }
  }, []);

  const armazemDepositoOptions = useMemo(
    () => armazensDeposito.map((item) => ({
      value: String(item.id),
      label: `${item.codigo || '-'} - ${item.nome || 'Sem nome'}`
    })),
    [armazensDeposito]
  );

  const enderecoDepositoOptions = useMemo(() => {
    if (!armazenamentoLoteForm.idDepositoEndereco || typeof armazenamentoLoteForm.idDepositoEndereco !== 'object') {
      return [];
    }

    return [armazenamentoLoteForm.idDepositoEndereco];
  }, [armazenamentoLoteForm.idDepositoEndereco]);

  const handleBuscarEnderecoDeposito = useCallback(
    async (inputValue) => {
      const idDepositoArmazem = parseNumber(armazenamentoLoteForm.idDepositoArmazem);
      const termo = String(inputValue || '').trim();

      if (!idDepositoArmazem || !termo) {
        return [];
      }

      const response = await getDepositoEnderecos({
        IdDepositoArmazem: idDepositoArmazem,
        Texto: termo,
        PaginaAtual: 1,
        QuantidadePorPagina: 50
      });

      return parsePaginacaoResponse(response).registros.map((item) => ({
        value: String(item.id),
        label: formatEnderecoDeposito(item)
      }));
    },
    [armazenamentoLoteForm.idDepositoArmazem]
  );

  const armazenagemLocalOptions = useMemo(
    () => armazenagemLocais.map((item) => ({
      value: String(item.id),
      label: `${item.codigo} - ${item.descricao || 'Sem descricao'}`
    })),
    [armazenagemLocais]
  );

  const syncLookupRefsFromMappedForm = useCallback((mapped) => {
    nomeClienteLookupRef.current = toDigitsOnly(mapped?.cnpjCliente || '') || '';
    chaveNfeLookupRef.current = toDigitsOnly(mapped?.chaveNFe || '') || '';
  }, []);

  const handleBuscarNomeCliente = useCallback(async (rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue : rawValue?.target?.value;
    const cnpj = toDigitsOnly(value || '');
    if (!cnpj || cnpj.length !== 14) return;
    if (nomeClienteLookupRef.current === cnpj) return;

    try {
      const response = await getNomeClientePorCnpj(cnpj);
      const nomeCliente = response?.nomeCliente || response?.data?.nomeCliente || '';

      if (nomeCliente) {
        formRef.current?.change('nomeCliente', nomeCliente);
        nomeClienteLookupRef.current = cnpj;
      }
    } catch (error) {
      console.error('Erro ao buscar nome do cliente:', error);
    }
  }, []);

  const handleConsultarNotaFiscalPorChave = useCallback(async (rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue : rawValue?.target?.value;
    const chave = toDigitsOnly(value || '');
    if (!chave || chave.length !== 44) return;
    if (chaveNfeLookupRef.current === chave) return;

    setConsultandoChaveNfe(true);
    try {
      const response = await consultarDepositoNotaFiscalPorChave(chave);
      const dados = response?.data || response;

      if (dados?.numeroNf) {
        formRef.current?.change('numeroNF', dados.numeroNf);
      }
      if (dados?.serieNf) {
        formRef.current?.change('serieNF', dados.serieNf);
      }
      if (dados?.cnpjEmitente) {
        formRef.current?.change('cnpjCliente', formatarCNPJ(dados.cnpjEmitente));
        nomeClienteLookupRef.current = dados.cnpjEmitente;
      }
      if (dados?.nomeEmitente) {
        formRef.current?.change('nomeCliente', dados.nomeEmitente);
        formRef.current?.change('nomeRemetente', dados.nomeEmitente);
      }
      if (dados?.nomeDestinatario) {
        formRef.current?.change('nomeDestinatario', dados.nomeDestinatario);
      }
      if (dados?.volumes !== null && dados?.volumes !== undefined) {
        formRef.current?.change('quantidadeTotalCaixas', String(dados.volumes));
      }

      chaveNfeLookupRef.current = chave;
    } catch (error) {
      chaveNfeLookupRef.current = '';
      notification.error(getApiErrorMessage(error, 'Erro ao consultar NF-e pela chave'));
    } finally {
      setConsultandoChaveNfe(false);
    }
  }, []);

  const handleChaveNfeKeyDown = useCallback((event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    handleConsultarNotaFiscalPorChave(event);
  }, [handleConsultarNotaFiscalPorChave]);

  const handleChaveNfeBlur = useCallback(async (rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue : rawValue?.target?.value;
    const sanitized = sanitizeChaveNFe(value);
    if (sanitized !== (value || '')) {
      formRef.current?.change('chaveNFe', sanitized);
    }
    await handleConsultarNotaFiscalPorChave(sanitized || value);
  }, [handleConsultarNotaFiscalPorChave]);

  const depositoFormStructureWithLookup = useMemo(
    () =>
      depositoNotaFiscalFormStructure.map((row) =>
        row.map((field) => {
          if (field?.name === 'cnpjCliente') {
            return {
              ...field,
              onBlur: handleBuscarNomeCliente
            };
          }
          if (field?.name === 'chaveNFe') {
            return {
              ...field,
              onBlur: handleChaveNfeBlur,
              onKeyDown: handleChaveNfeKeyDown,
              disabled: consultandoChaveNfe,
              readOnly: consultandoChaveNfe
            };
          }
          return field;
        })
      ),
    [handleBuscarNomeCliente, handleChaveNfeBlur, handleChaveNfeKeyDown, consultandoChaveNfe]
  );
  const {
    data,
    loading,
    filterLoading,
    totalRegistros,
    resumoPendencias,
    pendenciaHeader,
    paginationState,
    handlePaginationChange,
    handleFilterApply,
    handleLimparFiltros,
    handlePendenciaHeaderChange,
    fetchData,
    applyUpdatedNota
  } = useDepositoNotasListagem();
  const triggerAnexosReload = useCallback(() => {
    setAnexosReloadTrigger((prev) => prev + 1);
  }, []);
  const selectedIds = useMemo(() => selectedNotas.map((item) => item.id), [selectedNotas]);
  const cnpjSelecionado = useMemo(
    () => (selectedNotas.length ? toDigitsOnly(selectedNotas[0].cnpjCliente || '') : ''),
    [selectedNotas]
  );
  const previewNotasFinalizacaoLote = useMemo(() => selectedNotas.slice(0, 10), [selectedNotas]);
  const resumoFinalizacaoLoteErros = useMemo(
    () => (finalizacaoLoteResumo?.itens || []).filter((item) => !item.sucesso),
    [finalizacaoLoteResumo]
  );
  const previewNotasAlteracaoLote = useMemo(() => selectedNotas.slice(0, 10), [selectedNotas]);
  const resumoAlteracaoLoteErros = useMemo(
    () => (alteracaoLoteResumo?.itens || []).filter((item) => !item.sucesso),
    [alteracaoLoteResumo]
  );
  const previewNotasArmazenamentoLote = useMemo(() => selectedNotas.slice(0, 10), [selectedNotas]);
  const resumoArmazenamentoLoteErros = useMemo(
    () => (armazenamentoLoteResumo?.itens || []).filter((item) => !item.sucesso),
    [armazenamentoLoteResumo]
  );
  const pageSelectableNotas = useMemo(() => {
    if (!data.length) return [];
    if (!cnpjSelecionado) return data;
    return data.filter((item) => toDigitsOnly(item.cnpjCliente || '') === cnpjSelecionado);
  }, [data, cnpjSelecionado]);
  const allPageSelected = useMemo(
    () => pageSelectableNotas.length > 0 && pageSelectableNotas.every((item) => selectedIds.includes(item.id)),
    [pageSelectableNotas, selectedIds]
  );
  const selectAllDisabled = useMemo(
    () => !data.length || (cnpjSelecionado && pageSelectableNotas.length === 0),
    [data, cnpjSelecionado, pageSelectableNotas]
  );

  const resetInlineEdit = useCallback(() => {
    setInlineEditingCell(null);
    setInlineEditingValue('');
  }, []);

  const handleInlineEditStart = useCallback(
    (columnId, rowId) => {
      if (!rowId || inlineSavingCellKey) return;

      const row = data.find((item) => item.id === rowId);
      const config = inlineEditableColumns[columnId];
      if (!row || !config) return;

      const nextCellKey = buildInlineCellKey(rowId, columnId);
      const currentCellKey = inlineEditingCell ? buildInlineCellKey(inlineEditingCell.rowId, inlineEditingCell.columnId) : null;

      if (currentCellKey === nextCellKey || inlineEditingCell) return;

      const originalValue = config.formatInputValue(config.getOriginalValue(row));
      setInlineEditingCell({
        rowId,
        columnId,
        originalValue
      });
      setInlineEditingValue(originalValue);
    },
    [data, inlineEditingCell, inlineSavingCellKey]
  );

  const handleInlineEditCancel = useCallback(() => {
    if (inlineSavingCellKey) return;
    resetInlineEdit();
  }, [inlineSavingCellKey, resetInlineEdit]);

  const handleInlineEditCommit = useCallback(
    async ({ columnId, rowId, value: commitValue }) => {
      if (!rowId || !inlineEditingCell) return;

      const cellKey = buildInlineCellKey(rowId, columnId);
      const currentCellKey = buildInlineCellKey(inlineEditingCell.rowId, inlineEditingCell.columnId);
      if (cellKey !== currentCellKey || inlineSavingCellKey) return;

      const row = data.find((item) => item.id === rowId);
      const config = inlineEditableColumns[columnId];
      if (!row || !config) {
        resetInlineEdit();
        return;
      }

      const resolvedValue = commitValue !== undefined && commitValue !== null
        ? commitValue
        : inlineEditingValue;
      const nextValue = config.normalizeForCompare(resolvedValue);
      const previousValue = config.normalizeForCompare(inlineEditingCell.originalValue);
      if (nextValue === previousValue) {
        resetInlineEdit();
        return;
      }

      const payload = {
        [config.payloadKey]: config.serialize(nextValue)
      };

      try {
        setInlineSavingCellKey(cellKey);
        const response = await updateDepositoNotaFiscal(rowId, payload);
        const updatedNota = response?.data || response || {};
        const mappedRow = applyUpdatedNota(updatedNota);

        if (mappedRow) {
          setSelectedNotas((prev) => prev.map((item) => (item.id === mappedRow.id ? mappedRow : item)));
        }

        if (editingId === rowId) {
          const nextFormDataChegada = mappedRow?.dataChegadaOriginal ? toFormDateTime(mappedRow.dataChegadaOriginal) : '';
          const nextFormDataAgendamento = mappedRow?.dataAgendamentoOriginal ? toFormDateTime(mappedRow.dataAgendamentoOriginal) : '';
          const nextFormDataSaida = mappedRow?.dataSaidaOriginal ? toFormDateTime(mappedRow.dataSaidaOriginal) : '';
          setFormInitialValues((prev) => ({
            ...prev,
            dataChegada: nextFormDataChegada,
            dataAgendamento: nextFormDataAgendamento,
            dataSaida: nextFormDataSaida
          }));
          formRef.current?.change?.('dataChegada', nextFormDataChegada);
          formRef.current?.change?.('dataAgendamento', nextFormDataAgendamento);
          formRef.current?.change?.('dataSaida', nextFormDataSaida);
        }
      } catch (error) {
        notification.error(getApiErrorMessage(error, `Erro ao atualizar ${config.label}`));
        console.error(`Erro ao atualizar ${config.label} inline:`, error);
      } finally {
        setInlineSavingCellKey(null);
        resetInlineEdit();
      }
    },
    [applyUpdatedNota, data, editingId, inlineEditingCell, inlineEditingValue, inlineSavingCellKey, resetInlineEdit]
  );

  const inlineEditConfig = useMemo(
    () => ({
      editingCellKey: inlineEditingCell
        ? buildInlineCellKey(inlineEditingCell.rowId, inlineEditingCell.columnId)
        : null,
      savingCellKey: inlineSavingCellKey,
      editingInitialValue: inlineEditingCell
        ? inlineEditableColumns[inlineEditingCell.columnId]?.formatInputValue(inlineEditingCell.originalValue)
        : '',
      startEdit: handleInlineEditStart,
      commitEdit: ({ columnId, rowId, value }) => handleInlineEditCommit({ columnId, rowId, value }),
      cancelEdit: handleInlineEditCancel
    }),
    [
      handleInlineEditCancel,
      handleInlineEditCommit,
      handleInlineEditStart,
      inlineEditingCell,
      inlineSavingCellKey
    ]
  );
  const clearSelectedNotas = useCallback(() => {
    setSelectedNotas([]);
  }, []);

  const handleBuscarComResetSelecao = useCallback(
    (novosFiltros) => {
      clearSelectedNotas();
      handleFilterApply(novosFiltros);
    },
    [clearSelectedNotas, handleFilterApply]
  );

  const handleLimparFiltrosComResetSelecao = useCallback(
    (filtrosLimpos) => {
      clearSelectedNotas();
      handleLimparFiltros(filtrosLimpos);
    },
    [clearSelectedNotas, handleLimparFiltros]
  );

  const handlePendenciaHeaderComResetSelecao = useCallback(
    (nextPendenciaHeader) => {
      clearSelectedNotas();
      handlePendenciaHeaderChange(nextPendenciaHeader);
    },
    [clearSelectedNotas, handlePendenciaHeaderChange]
  );

  const handlePaginationChangeComResetSelecao = useCallback(
    (next) => {
      let nextPaginationState = paginationState;

      if (typeof next === 'function') {
        nextPaginationState = next(paginationState);
      } else if (typeof next === 'number') {
        nextPaginationState = { ...paginationState, pageIndex: next };
      } else if (next && typeof next === 'object') {
        nextPaginationState = { ...paginationState, ...next };
      }

      if ((nextPaginationState?.pageIndex ?? paginationState.pageIndex) !== paginationState.pageIndex) {
        clearSelectedNotas();
      }

      handlePaginationChange(next);
    },
    [clearSelectedNotas, handlePaginationChange, paginationState]
  );

  const resetToCreateForm = useCallback(() => {
    setEditingId(null);
    setFormInitialValues(emptyDepositoNotaFiscalForm);
    nomeClienteLookupRef.current = '';
    chaveNfeLookupRef.current = '';
    if (formRef.current) {
      formRef.current.reset(emptyDepositoNotaFiscalForm);
    }
    setIsFormCollapsed(false);
  }, []);

  const handleAdicionar = useCallback(() => {
    resetToCreateForm();
    setTimeout(() => formularioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, [resetToCreateForm]);

  const handleImportar = useCallback(() => {
    setShowImportarModal(true);
  }, []);

  const handleCloseImportar = useCallback(() => {
    setShowImportarModal(false);
  }, []);

  const handleClientes = useCallback(() => {
    setShowClientesModal(true);
  }, []);

  const handleCloseClientes = useCallback(() => {
    setShowClientesModal(false);
  }, []);

  const handleExportarPendencias = useCallback(async () => {
    try {
      setExportandoPendencias(true);
      const response = await exportarPendenciasDeposito({
        exportar: true,
        enviarPorEmail: false,
        emails: ''
      });

      const html = response?.data || response || '';
      if (!html) {
        notification.warning('Nenhum relatorio retornado.');
        return;
      }

      const reportWindow = window.open('', '_blank');
      if (!reportWindow) {
        notification.warning('Permita pop-ups para visualizar o relatorio.');
        return;
      }

      reportWindow.document.open();
      reportWindow.document.write(html);
      reportWindow.document.close();
    } catch (error) {
      notification.error('Erro ao exportar pendencias');
      console.error('Erro ao exportar pendencias:', error);
    } finally {
      setExportandoPendencias(false);
    }
  }, []);

  const handleMostrarDocumentacao = useCallback(async () => {
    try {
      setDocumentacaoLoading(true);
      const response = await obterDocumentacaoDepositoNotas();
      const html = response?.data || response || '';
      if (!html) {
        notification.warning('Nenhuma documentacao retornada.');
        return;
      }

      const docWindow = window.open('', '_blank');
      if (!docWindow) {
        notification.warning('Permita pop-ups para visualizar a documentacao.');
        return;
      }

      docWindow.document.open();
      docWindow.document.write(html);
      docWindow.document.close();
    } catch (error) {
      notification.error('Erro ao abrir documentacao');
      console.error('Erro ao abrir documentacao:', error);
    } finally {
      setDocumentacaoLoading(false);
    }
  }, []);

  const handleCobrancaErro = useCallback((error, fallback) => {
    const erros = parseCobrancaErros(error, fallback);
    setCobrancaErros(erros);
    setShowCobrancaErroModal(true);
  }, []);

  const handleCloseCobrancaErroModal = useCallback(() => {
    setShowCobrancaErroModal(false);
    setCobrancaErros([]);
  }, []);

  const handleToggleSelectNota = useCallback((row) => {
    if (!row) return;

    setSelectedNotas((prev) => {
      const exists = prev.some((item) => item.id === row.id);
      if (exists) {
        return prev.filter((item) => item.id !== row.id);
      }

      const cnpjBase = prev.length > 0 ? toDigitsOnly(prev[0].cnpjCliente || '') : '';
      const cnpjRow = toDigitsOnly(row.cnpjCliente || '');

      if (cnpjBase && cnpjRow && cnpjBase !== cnpjRow) {
        notification.warning('Selecione apenas notas do mesmo CNPJCliente para gerar cobrancas em lote.');
        return prev;
      }

      if (cnpjBase && !cnpjRow) {
        notification.warning('Selecione apenas notas do mesmo CNPJCliente para gerar cobrancas em lote.');
        return prev;
      }

      return [...prev, row];
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    if (!data.length) return;

    setSelectedNotas((prev) => {
      const cnpjBase = prev.length > 0 ? toDigitsOnly(prev[0].cnpjCliente || '') : '';
      const cnpjsPagina = Array.from(
        new Set(
          data
            .map((item) => toDigitsOnly(item.cnpjCliente || ''))
            .filter((cnpj) => cnpj)
        )
      );

      if (!cnpjBase) {
        if (cnpjsPagina.length > 1) {
          notification.warning('Selecione apenas notas do mesmo CNPJCliente para gerar cobrancas em lote.');
          return prev;
        }

        const cnpjPagina = cnpjsPagina[0] || '';
        if (!cnpjPagina) {
          notification.warning('Nao foi possivel selecionar notas sem CNPJCliente.');
          return prev;
        }

        const jaSelecionadas = data.every((item) => prev.some((nota) => nota.id === item.id));
        if (jaSelecionadas) {
          return prev.filter((nota) => !data.some((item) => item.id === nota.id));
        }

        const novas = data.filter((item) => !prev.some((nota) => nota.id === item.id));
        return [...prev, ...novas];
      }

      const notasSelecionaveis = data.filter(
        (item) => toDigitsOnly(item.cnpjCliente || '') === cnpjBase
      );

      if (!notasSelecionaveis.length) {
        notification.warning('Nao ha notas deste CNPJCliente na pagina atual.');
        return prev;
      }

      const todasSelecionadas = notasSelecionaveis.every((item) => prev.some((nota) => nota.id === item.id));
      if (todasSelecionadas) {
        return prev.filter((nota) => !notasSelecionaveis.some((item) => item.id === nota.id));
      }

      const novas = notasSelecionaveis.filter((item) => !prev.some((nota) => nota.id === item.id));
      return [...prev, ...novas];
    });
  }, [data]);

  const handleEditNota = useCallback(
    async (rowOrId) => {
      const id = typeof rowOrId === 'object' ? rowOrId?.id : rowOrId;
      if (!id || loadingEditId === id) return;

      if (typeof rowOrId === 'object' && rowOrId) {
        const mapped = mapRowToFormValues(rowOrId);
        setEditingId(id);
        setFormInitialValues(mapped);
        syncLookupRefsFromMappedForm(mapped);
        if (formRef.current) {
          formRef.current.reset(mapped);
        }
        setIsFormCollapsed(false);
        setTimeout(() => formularioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      }

      try {
        setLoadingEditId(id);
        const response = await getDepositoNotaFiscalById(id);
        const data = response?.data || response || {};
        setEditingId(id);
        const mapped = mapApiToFormValues(data);
        syncLookupRefsFromMappedForm(mapped);
        setFormInitialValues(mapped);
        if (formRef.current) {
          formRef.current.reset(mapped);
        }
        setIsFormCollapsed(false);
        setTimeout(() => formularioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      } catch (error) {
        notification.error('Erro ao carregar nota de deposito');
        console.error('Erro ao carregar nota de deposito:', error);
      } finally {
        setLoadingEditId(null);
      }
    },
    [loadingEditId]
  );

  const carregarNotaParaAcao = useCallback(
    async (row, acao) => {
      const id = typeof row === 'object' ? row?.id : row;
      if (!id || (loadingAcao?.id === id && loadingAcao?.acao === acao)) return null;

      try {
        setLoadingAcao({ id, acao });
        const response = await getDepositoNotaFiscalById(id);
        const data = response?.data || response || {};
        const mapped = mapApiToFormValues(data);
        setEditingId(id);
        setFormInitialValues(mapped);
        if (formRef.current) {
          formRef.current.reset(mapped);
        }
        return mapped;
      } catch (error) {
        notification.error('Erro ao carregar nota de deposito');
        console.error('Erro ao carregar nota de deposito:', error);
        return null;
      } finally {
        setLoadingAcao(null);
      }
    },
    [loadingAcao]
  );

  const abrirDivergenciaModal = useCallback(
    async (row) => {
      const mapped = await carregarNotaParaAcao(row, 'divergencia');
      if (!mapped) return;
      setDivergenciaMensagem('');
      setDivergenciaArquivos([]);
      setShowDivergenciaModal(true);
    },
    [carregarNotaParaAcao]
  );

  const abrirAnexoModal = useCallback(
    async (row) => {
      const mapped = await carregarNotaParaAcao(row, 'anexo');
      if (!mapped) return;
      setAnexoArquivos([]);
      setShowAnexoModal(true);
    },
    [carregarNotaParaAcao]
  );

  const abrirCanhotoModal = useCallback(
    async (row) => {
      const mapped = await carregarNotaParaAcao(row, 'canhoto');
      if (!mapped) return;
      setCanhotoArquivo(null);
      setCanhotoSerieNF(mapped.serieNF || '');
      setShowCanhotoModal(true);
    },
    [carregarNotaParaAcao]
  );

  const handleCloseVisualizarCanhotoModal = useCallback(() => {
    setShowVisualizarCanhotoModal(false);
    setCanhotoPreviewSrc('');
    setCanhotoPreviewTitulo('');
  }, []);

  const handleVisualizarCanhoto = useCallback(async (row) => {
    const notaId = row?.id;
    if (!notaId) {
      notification.warning('Selecione uma nota para visualizar o canhoto.');
      return;
    }

    if (loadingAcao?.id === notaId && loadingAcao?.acao === 'visualizar-canhoto') {
      return;
    }

    try {
      setLoadingAcao({ id: notaId, acao: 'visualizar-canhoto' });
      const response = await consultarDepositoCanhotoPorNotaId(notaId);
      const dados = response?.data || response;
      const arquivo = dados?.arquivo || dados?.Arquivo;
      const extensao = dados?.extensao || dados?.Extensao;
      const previewSrc = buildCanhotoPreviewSrc(arquivo, extensao);

      if (!previewSrc) {
        notification.warning('Canhoto não disponível para visualização');
        return;
      }

      const numeroNF = row?.numeroNF || row?.NumeroNF || '';
      setCanhotoPreviewTitulo(numeroNF ? `NF ${numeroNF}` : '');
      setCanhotoPreviewSrc(previewSrc);
      setShowVisualizarCanhotoModal(true);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao consultar canhoto'));
    } finally {
      setLoadingAcao(null);
    }
  }, [loadingAcao]);

  const abrirFinalizarModal = useCallback(
    async (row) => {
      const mapped = await carregarNotaParaAcao(row, 'finalizar');
      if (!mapped) return;
      setShowFinalizarModal(true);
    },
    [carregarNotaParaAcao]
  );

  const abrirAuditoriaModal = useCallback(
    async (row) => {
      const mapped = await carregarNotaParaAcao(row, 'auditoria');
      if (!mapped) return;
      setAuditoriaPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setShowAuditoriaModal(true);
    },
    [carregarNotaParaAcao]
  );

  const handleClear = useCallback(() => {
    resetToCreateForm();
  }, [resetToCreateForm]);

  const handleCloseDivergenciaModal = useCallback((force = false) => {
    if (divergenciaSalvando && !force) return;
    setShowDivergenciaModal(false);
    setDivergenciaMensagem('');
    setDivergenciaArquivos([]);
  }, [divergenciaSalvando]);

  const handleCloseAnexoModal = useCallback((force = false) => {
    if (anexoSalvando && !force) return;
    setShowAnexoModal(false);
    setAnexoArquivos([]);
  }, [anexoSalvando]);

  const handleAbrirAnexosModal = useCallback(() => {
    if (!editingId) return;
    setShowAnexosListModal(true);
  }, [editingId]);

  const handleCloseAnexosModal = useCallback(() => {
    setShowAnexosListModal(false);
  }, []);

  const handleAbrirCobrancasModal = useCallback(() => {
    if (!editingId) return;
    setShowCobrancasListModal(true);
  }, [editingId]);

  const handleCloseCobrancasModal = useCallback(() => {
    setShowCobrancasListModal(false);
  }, []);

  const handleCloseCanhotoModal = useCallback((force = false) => {
    if (canhotoSalvando && !force) return;
    setShowCanhotoModal(false);
    setCanhotoArquivo(null);
  }, [canhotoSalvando]);

  const handleCloseFinalizarModal = useCallback((force = false) => {
    if (finalizarSalvando && !force) return;
    setShowFinalizarModal(false);
  }, [finalizarSalvando]);

  const handleCloseAuditoriaModal = useCallback(() => {
    setShowAuditoriaModal(false);
  }, []);

  const handleAbrirAuditoriaGeralModal = useCallback(() => {
    setShowAuditoriaGeralModal(true);
  }, []);

  const handleCloseAuditoriaGeralModal = useCallback(() => {
    setShowAuditoriaGeralModal(false);
  }, []);

  const handleAbrirAuditoriaTabelaPrecoModal = useCallback(() => {
    setShowAuditoriaTabelaPrecoModal(true);
  }, []);

  const handleCloseAuditoriaTabelaPrecoModal = useCallback(() => {
    setShowAuditoriaTabelaPrecoModal(false);
  }, []);

  const handleAbrirFinalizarLoteModal = useCallback(() => {
    if (!selectedNotas.length) {
      notification.warning('Selecione ao menos uma nota para finalizar em lote.');
      return;
    }

    setShowFinalizarLoteModal(true);
  }, [selectedNotas]);

  const handleCloseFinalizarLoteModal = useCallback((force = false) => {
    if (finalizarLoteSalvando && !force) return;
    setShowFinalizarLoteModal(false);
  }, [finalizarLoteSalvando]);

  const handleCloseFinalizarLoteResumoModal = useCallback(() => {
    setShowFinalizarLoteResumoModal(false);
    setFinalizacaoLoteResumo(null);
  }, []);

  const handleAbrirAlteracaoLoteModal = useCallback(() => {
    if (!selectedNotas.length) {
      notification.warning('Selecione ao menos uma nota para alterar em lote.');
      return;
    }

    setAlteracaoLoteForm(emptyAlteracaoLoteForm);
    setShowAlteracaoLoteModal(true);
  }, [selectedNotas]);

  const handleCloseAlteracaoLoteModal = useCallback((force = false) => {
    if (alteracaoLoteSalvando && !force) return;
    setShowAlteracaoLoteModal(false);
    setAlteracaoLoteForm(emptyAlteracaoLoteForm);
  }, [alteracaoLoteSalvando]);

  const handleCloseAlteracaoLoteResumoModal = useCallback(() => {
    setShowAlteracaoLoteResumoModal(false);
    setAlteracaoLoteResumo(null);
  }, []);

  const handleAbrirArmazenamentoLoteModal = useCallback(async () => {
    if (!selectedNotas.length) {
      notification.warning('Selecione ao menos uma nota para armazenar em lote.');
      return;
    }

    setArmazenamentoLoteForm(emptyArmazenamentoLoteForm);
    try {
      await carregarArmazenamentoLookups();
      setShowArmazenamentoLoteModal(true);
    } catch (error) {
      console.error('Erro ao abrir modal de armazenamento:', error);
    }
  }, [carregarArmazenamentoLookups, selectedNotas]);

  const handleCloseArmazenamentoLoteModal = useCallback((force = false) => {
    if (armazenamentoLoteSalvando && !force) return;
    setShowArmazenamentoLoteModal(false);
    setArmazenamentoLoteForm(emptyArmazenamentoLoteForm);
  }, [armazenamentoLoteSalvando]);

  const handleCloseArmazenamentoLoteResumoModal = useCallback(() => {
    setShowArmazenamentoLoteResumoModal(false);
    setArmazenamentoLoteResumo(null);
  }, []);

  const handleArmazenamentoLoteFieldChange = useCallback((field) => (event) => {
    const value = event?.target?.value ?? '';
    setArmazenamentoLoteForm((prev) => {
      if (field === 'idDepositoArmazem') {
        return {
          ...prev,
          idDepositoArmazem: value,
          idDepositoEndereco: ''
        };
      }

      return {
        ...prev,
        [field]: value
      };
    });
  }, []);

  const handleArmazenamentoLoteEnderecoChange = useCallback((selectedOption) => {
    setArmazenamentoLoteForm((prev) => ({
      ...prev,
      idDepositoEndereco: selectedOption || ''
    }));
  }, []);

  const handleAlteracaoLoteAplicarChange = useCallback((field) => (event) => {
    const checked = !!event?.target?.checked;

    setAlteracaoLoteForm((prev) => ({
      ...prev,
      [field]: checked
    }));
  }, []);

  const handleAlteracaoLoteFieldChange = useCallback((field) => (event) => {
    const target = event?.target;
    const value = target?.type === 'checkbox' ? !!target.checked : target?.value;

    setAlteracaoLoteForm((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleSalvarAlteracaoLote = useCallback(async () => {
    if (!selectedNotas.length) {
      notification.warning('Selecione ao menos uma nota para alterar em lote.');
      return;
    }

    const payload = {
      Ids: selectedNotas.map((item) => item.id).filter(Boolean),
      CamposAplicados: []
    };

    const addCampo = (applyKey, fieldName, value) => {
      if (!alteracaoLoteForm[applyKey]) return;
      payload.CamposAplicados.push(fieldName);
      payload[fieldName] = value;
    };

    addCampo('aplicarFinalizado', 'Finalizado', !!alteracaoLoteForm.finalizado);
    addCampo('aplicarExisteDivergencia', 'ExisteDivergencia', !!alteracaoLoteForm.existeDivergencia);
    addCampo('aplicarDataSaida', 'DataSaida', alteracaoLoteForm.dataSaida ? toApiDateTime(alteracaoLoteForm.dataSaida) : null);
    addCampo(
      'aplicarDataAgendamento',
      'DataAgendamento',
      alteracaoLoteForm.dataAgendamento ? toApiDateTime(alteracaoLoteForm.dataAgendamento) : null
    );
    addCampo('aplicarDataChegada', 'DataChegada', alteracaoLoteForm.dataChegada ? toApiDateTime(alteracaoLoteForm.dataChegada) : null);
    addCampo('aplicarDataLeitura', 'DataLeitura', alteracaoLoteForm.dataLeitura ? toApiDateTime(alteracaoLoteForm.dataLeitura) : null);
    addCampo(
      'aplicarDataConferencia',
      'DataConferencia',
      alteracaoLoteForm.dataConferencia ? toApiDateTime(alteracaoLoteForm.dataConferencia) : null
    );
    addCampo(
      'aplicarDataArmazenagem',
      'DataArmazenagem',
      alteracaoLoteForm.dataArmazenagem ? toApiDateTime(alteracaoLoteForm.dataArmazenagem) : null
    );
    addCampo('aplicarTipoOperacao', 'TipoOperacao', normalizeText(alteracaoLoteForm.tipoOperacao));
    addCampo('aplicarObservacoes', 'Observacoes', normalizeText(alteracaoLoteForm.observacoes));
    addCampo(
      'aplicarCobrancaPaletizacaoRealizada',
      'CobrancaPaletizacaoRealizada',
      !!alteracaoLoteForm.cobrancaPaletizacaoRealizada
    );
    addCampo(
      'aplicarCobrancaEtiquetagemRealizada',
      'CobrancaEtiquetagemRealizada',
      !!alteracaoLoteForm.cobrancaEtiquetagemRealizada
    );
    addCampo(
      'aplicarCobrancaArmazenagemRealizada',
      'CobrancaArmazenagemRealizada',
      !!alteracaoLoteForm.cobrancaArmazenagemRealizada
    );

    if (!payload.CamposAplicados.length) {
      notification.warning('Selecione ao menos um campo para aplicar na alteracao em lote.');
      return;
    }

    try {
      setAlteracaoLoteSalvando(true);
      const resumo = normalizeAlteracaoLoteResumo(await alterarDepositoNotaFiscalEmLote(payload));

      setAlteracaoLoteResumo(resumo);
      setShowAlteracaoLoteResumoModal(true);
      clearSelectedNotas();
      fetchData();
      handleCloseAlteracaoLoteModal(true);

      if (resumo.quantidadeErro > 0) {
        notification.warning(
          `Alteracao em lote concluida com ${resumo.quantidadeSucesso} sucesso(s) e ${resumo.quantidadeErro} erro(s).`
        );
      } else {
        notification.success(`Alteracao em lote concluida com ${resumo.quantidadeSucesso} nota(s).`);
      }
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao alterar notas em lote'));
      console.error('Erro ao alterar notas em lote:', error);
    } finally {
      setAlteracaoLoteSalvando(false);
    }
  }, [selectedNotas, alteracaoLoteForm, clearSelectedNotas, fetchData, handleCloseAlteracaoLoteModal]);

  const handleSalvarArmazenamentoLote = useCallback(async () => {
    if (!selectedNotas.length) {
      notification.warning('Selecione ao menos uma nota para armazenar em lote.');
      return;
    }

    const idDepositoArmazem = parseNumber(armazenamentoLoteForm.idDepositoArmazem);
    const idDepositoEndereco = parseNumber(armazenamentoLoteForm.idDepositoEndereco);
    const idDepositoArmazenagemLocal = parseNumber(armazenamentoLoteForm.idDepositoArmazenagemLocal);

    if (!idDepositoArmazem) {
      notification.warning('Selecione o armazem.');
      return;
    }

    if (!idDepositoEndereco) {
      notification.warning('Selecione o endereco.');
      return;
    }

    if (!idDepositoArmazenagemLocal) {
      notification.warning('Selecione o local de armazenamento.');
      return;
    }

    const payload = {
      Ids: selectedNotas.map((item) => item.id).filter(Boolean),
      CamposAplicados: ['IdDepositoEndereco', 'IdDepositoArmazenagemLocal'],
      IdDepositoArmazem: idDepositoArmazem,
      IdDepositoEndereco: idDepositoEndereco,
      IdDepositoArmazenagemLocal: idDepositoArmazenagemLocal
    };

    try {
      setArmazenamentoLoteSalvando(true);
      const resumo = normalizeAlteracaoLoteResumo(await alterarDepositoNotaFiscalEmLote(payload));

      setArmazenamentoLoteResumo(resumo);
      setShowArmazenamentoLoteResumoModal(true);
      clearSelectedNotas();
      fetchData();
      handleCloseArmazenamentoLoteModal(true);

      if (resumo.quantidadeErro > 0) {
        notification.warning(
          `Armazenamento em lote concluido com ${resumo.quantidadeSucesso} sucesso(s) e ${resumo.quantidadeErro} erro(s).`
        );
      } else {
        notification.success(`Armazenamento em lote concluido com ${resumo.quantidadeSucesso} nota(s).`);
      }
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao armazenar notas em lote'));
      console.error('Erro ao armazenar notas em lote:', error);
    } finally {
      setArmazenamentoLoteSalvando(false);
    }
  }, [armazenamentoLoteForm, clearSelectedNotas, fetchData, handleCloseArmazenamentoLoteModal, selectedNotas]);

  const handleSalvarDivergencia = useCallback(async () => {
    if (!editingId) {
      notification.warning('Selecione uma nota para cadastrar divergencia.');
      return;
    }

    const mensagem = divergenciaMensagem.trim();
    if (!mensagem) {
      notification.warning('Informe a mensagem da divergencia.');
      return;
    }

    try {
      setDivergenciaSalvando(true);
      const anexosPayload = divergenciaArquivos.length
        ? await buildAnexosPayload(divergenciaArquivos)
        : [];

      const payload = {
        Mensagem: mensagem
      };

      if (anexosPayload.length) {
        payload.Anexos = anexosPayload;
      }

      const response = await cadastrarDivergenciaDepositoNotaFiscal(editingId, payload);
      const data = response?.data || response || {};
      const mapped = mapApiToFormValues(data);
      setFormInitialValues(mapped);
      if (formRef.current) {
        formRef.current.reset(mapped);
      }
      fetchData();
      triggerAnexosReload();
      handleCloseDivergenciaModal(true);
      notification.success('Divergencia cadastrada com sucesso!');
    } catch (error) {
      notification.error('Erro ao cadastrar divergencia');
      console.error('Erro ao cadastrar divergencia:', error);
    } finally {
      setDivergenciaSalvando(false);
    }
  }, [editingId, divergenciaMensagem, divergenciaArquivos, fetchData, triggerAnexosReload, handleCloseDivergenciaModal]);

  const handleSalvarAnexos = useCallback(async () => {
    if (!editingId) {
      notification.warning('Selecione uma nota para enviar anexos.');
      return;
    }

    if (!anexoArquivos.length) {
      notification.warning('Selecione ao menos um anexo.');
      return;
    }

    try {
      setAnexoSalvando(true);
      const anexosPayload = await buildAnexosPayload(anexoArquivos);
      if (!anexosPayload.length) {
        notification.warning('Nenhum anexo valido para enviar.');
        return;
      }

      await enviarAnexosDepositoNotaFiscal(editingId, { Anexos: anexosPayload });
      fetchData();
      triggerAnexosReload();
      handleCloseAnexoModal(true);
      notification.success('Anexos enviados com sucesso!');
    } catch (error) {
      notification.error('Erro ao enviar anexos');
      console.error('Erro ao enviar anexos:', error);
    } finally {
      setAnexoSalvando(false);
    }
  }, [editingId, anexoArquivos, fetchData, triggerAnexosReload, handleCloseAnexoModal]);

  const handleSalvarCanhoto = useCallback(async () => {
    if (!editingId) {
      notification.warning('Selecione uma nota para enviar canhoto.');
      return;
    }

    if (!canhotoArquivo) {
      notification.warning('Selecione a imagem do canhoto.');
      return;
    }

    const serieNF = canhotoSerieNF.trim();
    if (!serieNF) {
      notification.warning('Informe a serie da NF.');
      return;
    }

    try {
      setCanhotoSalvando(true);
      const base64 = await readFileAsBase64(canhotoArquivo);

      const payload = {
        ImagemCanhotoBase64: base64,
        NomeArquivo: canhotoArquivo.name,
        ContentType: canhotoArquivo.type || 'image/png',
        SerieNF: serieNF
      };

      const response = await enviarCanhotoDepositoNotaFiscal(editingId, payload);
      const data = response?.data || response || {};
      const mapped = mapApiToFormValues(data);
      setFormInitialValues(mapped);
      if (formRef.current) {
        formRef.current.reset(mapped);
      }
      fetchData();
      triggerAnexosReload();
      handleCloseCanhotoModal(true);
      notification.success('Canhoto enviado com sucesso!');
    } catch (error) {
      notification.error('Erro ao enviar canhoto');
      console.error('Erro ao enviar canhoto:', error);
    } finally {
      setCanhotoSalvando(false);
    }
  }, [editingId, canhotoArquivo, canhotoSerieNF, fetchData, triggerAnexosReload, handleCloseCanhotoModal]);

  const handleFinalizarNota = useCallback(async () => {
    if (!editingId) {
      notification.warning('Selecione uma nota para finalizar.');
      return;
    }

    try {
      setFinalizarSalvando(true);
      const response = await finalizarDepositoNotaFiscal(editingId);
      const data = response?.data || response || {};
      const mapped = mapApiToFormValues(data);
      setFormInitialValues(mapped);
      if (formRef.current) {
        formRef.current.reset(mapped);
      }
      fetchData();
      handleCloseFinalizarModal(true);
      notification.success('Nota finalizada com sucesso!');
    } catch (error) {
      notification.error('Erro ao finalizar nota');
      console.error('Erro ao finalizar nota:', error);
    } finally {
      setFinalizarSalvando(false);
    }
  }, [editingId, fetchData, handleCloseFinalizarModal]);

  const handleFinalizarNotasEmLote = useCallback(async () => {
    if (!selectedNotas.length) {
      notification.warning('Selecione ao menos uma nota para finalizar em lote.');
      return;
    }

    try {
      setFinalizarLoteSalvando(true);
      const ids = selectedNotas.map((item) => item.id).filter(Boolean);
      const resumo = normalizeFinalizacaoLoteResumo(await finalizarDepositoNotaFiscalEmLote(ids));

      setFinalizacaoLoteResumo(resumo);
      setShowFinalizarLoteResumoModal(true);
      clearSelectedNotas();
      fetchData();
      handleCloseFinalizarLoteModal(true);

      if (resumo.quantidadeErro > 0) {
        notification.warning(
          `Finalizacao em lote concluida com ${resumo.quantidadeSucesso} sucesso(s) e ${resumo.quantidadeErro} erro(s).`
        );
      } else {
        notification.success(`Finalizacao em lote concluida com ${resumo.quantidadeSucesso} nota(s).`);
      }
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao finalizar notas em lote'));
      console.error('Erro ao finalizar notas em lote:', error);
    } finally {
      setFinalizarLoteSalvando(false);
    }
  }, [selectedNotas, clearSelectedNotas, fetchData, handleCloseFinalizarLoteModal]);

  const fetchAuditoria = useCallback(async () => {
    if (!showAuditoriaModal || !editingId) {
      setAuditoriaData([]);
      setAuditoriaTotal(0);
      return;
    }

    try {
      setAuditoriaLoading(true);
      const response = await getDepositoNotaFiscalAuditoria(editingId, {
        PaginaAtual: auditoriaPagination.pageIndex + 1,
        QuantidadePorPagina: auditoriaPagination.pageSize
      });
      const auditoria = normalizeAuditoriaResponse(response);
      setAuditoriaData(auditoria.registros);
      setAuditoriaTotal(auditoria.totalRegistros);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar auditoria da nota'));
      console.error('Erro ao carregar auditoria da nota:', error);
      setAuditoriaData([]);
      setAuditoriaTotal(0);
    } finally {
      setAuditoriaLoading(false);
    }
  }, [showAuditoriaModal, editingId, auditoriaPagination.pageIndex, auditoriaPagination.pageSize]);

  const handleAuditoriaPagination = useCallback((next) => {
    setAuditoriaPagination((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  const fetchAuditoriaGeral = useCallback(async () => {
    if (!showAuditoriaGeralModal) {
      setAuditoriaGeralData([]);
      setAuditoriaGeralTotal(0);
      return;
    }

    try {
      setAuditoriaGeralLoading(true);
      const response = await getDepositoNotasFiscaisAuditoria({
        NumeroNF: normalizeText(auditoriaGeralFiltros.NumeroNF) || undefined,
        CNPJCliente: toDigitsOnly(auditoriaGeralFiltros.CNPJCliente) || undefined,
        DataInicial: toApiDateTime(auditoriaGeralFiltros.DataInicial) || undefined,
        DataFinal: toApiDateTime(auditoriaGeralFiltros.DataFinal) || undefined,
        Acao: extractValue(auditoriaGeralFiltros.Acao) || undefined,
        Usuario: normalizeText(auditoriaGeralFiltros.Usuario) || undefined,
        PaginaAtual: auditoriaGeralPagination.pageIndex + 1,
        QuantidadePorPagina: auditoriaGeralPagination.pageSize
      });
      const auditoria = normalizeAuditoriaResponse(response);
      setAuditoriaGeralData(auditoria.registros);
      setAuditoriaGeralTotal(auditoria.totalRegistros);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar auditoria geral'));
      console.error('Erro ao carregar auditoria geral:', error);
      setAuditoriaGeralData([]);
      setAuditoriaGeralTotal(0);
    } finally {
      setAuditoriaGeralLoading(false);
    }
  }, [showAuditoriaGeralModal, auditoriaGeralFiltros, auditoriaGeralPagination.pageIndex, auditoriaGeralPagination.pageSize]);

  const handleAuditoriaGeralFiltros = useCallback((filters = {}) => {
    setAuditoriaGeralFiltros(filters);
    setAuditoriaGeralPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleAuditoriaGeralPagination = useCallback((next) => {
    setAuditoriaGeralPagination((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  const fetchAuditoriaTabelaPreco = useCallback(async () => {
    if (!showAuditoriaTabelaPrecoModal) {
      setAuditoriaTabelaPrecoData([]);
      setAuditoriaTabelaPrecoTotal(0);
      return;
    }

    try {
      setAuditoriaTabelaPrecoLoading(true);
      const response = await getDepositoTabelasPrecoAuditoria({
        CNPJCliente: toDigitsOnly(auditoriaTabelaPrecoFiltros.CNPJCliente) || undefined,
        NomeCliente: normalizeText(auditoriaTabelaPrecoFiltros.NomeCliente) || undefined,
        Nome: normalizeText(auditoriaTabelaPrecoFiltros.Nome) || undefined,
        DataInicial: toApiDateTime(auditoriaTabelaPrecoFiltros.DataInicial) || undefined,
        DataFinal: toApiDateTime(auditoriaTabelaPrecoFiltros.DataFinal) || undefined,
        Acao: extractValue(auditoriaTabelaPrecoFiltros.Acao) || undefined,
        Usuario: normalizeText(auditoriaTabelaPrecoFiltros.Usuario) || undefined,
        Responsavel: normalizeText(auditoriaTabelaPrecoFiltros.Responsavel) || undefined,
        PaginaAtual: auditoriaTabelaPrecoPagination.pageIndex + 1,
        QuantidadePorPagina: auditoriaTabelaPrecoPagination.pageSize
      });
      const auditoria = normalizeAuditoriaTabelaPrecoResponse(response);
      setAuditoriaTabelaPrecoData(auditoria.registros);
      setAuditoriaTabelaPrecoTotal(auditoria.totalRegistros);
    } catch (error) {
      notification.error(getApiErrorMessage(error, 'Erro ao carregar auditoria de tabela de preco'));
      console.error('Erro ao carregar auditoria de tabela de preco:', error);
      setAuditoriaTabelaPrecoData([]);
      setAuditoriaTabelaPrecoTotal(0);
    } finally {
      setAuditoriaTabelaPrecoLoading(false);
    }
  }, [showAuditoriaTabelaPrecoModal, auditoriaTabelaPrecoFiltros, auditoriaTabelaPrecoPagination.pageIndex, auditoriaTabelaPrecoPagination.pageSize]);

  const handleAuditoriaTabelaPrecoFiltros = useCallback((filters = {}) => {
    setAuditoriaTabelaPrecoFiltros(filters);
    setAuditoriaTabelaPrecoPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleAuditoriaTabelaPrecoPagination = useCallback((next) => {
    setAuditoriaTabelaPrecoPagination((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  useEffect(() => {
    fetchAuditoria();
  }, [fetchAuditoria]);

  useEffect(() => {
    fetchAuditoriaGeral();
  }, [fetchAuditoriaGeral]);

  useEffect(() => {
    fetchAuditoriaTabelaPreco();
  }, [fetchAuditoriaTabelaPreco]);

  const abrirModalCobranca = useCallback(
    async (tipo, idOverride, notaOverride) => {
      const targetId = idOverride || editingId;
      if (!targetId) {
        notification.warning('Selecione uma nota de deposito para gerar cobranca.');
        return;
      }

      const notaSelecionada = notaOverride || data.find((item) => item.id === targetId);
      const notaFallback = {
        id: editingId || targetId,
        numeroNF: formInitialValues.numeroNF,
        serieNF: formInitialValues.serieNF,
        cnpjCliente: formInitialValues.cnpjCliente,
        cobrancaPaletizacaoRealizada: formInitialValues.cobrancaPaletizacaoRealizada,
        cobrancaEtiquetagemRealizada: formInitialValues.cobrancaEtiquetagemRealizada,
        cobrancaArmazenagemRealizada: formInitialValues.cobrancaArmazenagemRealizada,
        protocoloPedidoTMSPaletizacao: formInitialValues.protocoloPedidoTMSPaletizacao,
        protocoloPedidoTMSEtiquetagem: formInitialValues.protocoloPedidoTMSEtiquetagem
      };
      const notasSelecionadas = notaSelecionada ? [notaSelecionada] : [notaFallback];

      setCobrancaTipo(tipo);
      setCobrancaLoading(true);
      setCobrancaRecalculando(false);
      setCobrancaCalculo(null);
      setCobrancaFormInitialValues({ valorTotal: '', numeroPedidoEmbarcador: '', observacoes: '', tipo: '', dataCobranca: '' });
      setShowCobrancaModal(false);
      setShowCobrancaErroModal(false);
      setCobrancaErros([]);
      setCobrancaNotasSelecionadas(notasSelecionadas);
      const numeroPedido = buildNumeroPedidoEmbarcador(notasSelecionadas, tipo);
      try {
        let response;
        const ids = notasSelecionadas.map((item) => item.id).filter(Boolean);
        if (tipo === 'paletizacao') {
          response = await calcularCobrancaPaletizacao(ids);
        } else if (tipo === 'etiquetagem') {
          response = await calcularCobrancaEtiquetagem(ids);
        } else if (tipo === 'avulsa') {
          response = await calcularCobrancaAvulsa(ids);
        } else {
          response = await calcularCobrancaArmazenagem(ids);
        }

        const calculo = response?.data || response || null;
        setCobrancaCalculo(calculo);
        const valorTotal = calculo?.valorTotal ?? '';
        setCobrancaFormInitialValues({
          valorTotal: tipo === 'avulsa' ? '' : valorTotal !== null && valorTotal !== undefined ? valorTotal : '',
          numeroPedidoEmbarcador: numeroPedido || '',
          observacoes: buildObservacoesCobranca(calculo),
          tipo: '',
          dataCobranca: ''
        });
        setShowCobrancaModal(true);
      } catch (error) {
        handleCobrancaErro(error, 'Erro ao calcular cobranca');
        console.error('Erro ao calcular cobranca:', error);
      } finally {
        setCobrancaLoading(false);
      }
    },
    [editingId, data, formInitialValues, handleCobrancaErro]
  );

  const handleNovaCobrancaEmLote = useCallback(
    async (tipo) => {
      if (!selectedNotas.length) {
        notification.warning('Selecione ao menos uma nota para gerar cobranca.');
        return;
      }

      setCobrancaTipo(tipo);
      setBulkCobrancaLoading(tipo);
      setCobrancaLoading(true);
      setCobrancaRecalculando(false);
      setCobrancaCalculo(null);
      setCobrancaFormInitialValues({ valorTotal: '', numeroPedidoEmbarcador: '', observacoes: '', tipo: '', dataCobranca: '' });
      setShowCobrancaModal(false);
      setShowCobrancaErroModal(false);
      setCobrancaErros([]);
      setCobrancaNotasSelecionadas(selectedNotas);
      const numeroPedido = buildNumeroPedidoEmbarcador(selectedNotas, tipo);
      try {
        const ids = selectedNotas.map((item) => item.id);
        let response;
        if (tipo === 'paletizacao') {
          response = await calcularCobrancaPaletizacao(ids);
        } else if (tipo === 'etiquetagem') {
          response = await calcularCobrancaEtiquetagem(ids);
        } else if (tipo === 'avulsa') {
          response = await calcularCobrancaAvulsa(ids);
        } else {
          response = await calcularCobrancaArmazenagem(ids);
        }

        const calculo = response?.data || response || null;
        setCobrancaCalculo(calculo);
        const valorTotal = calculo?.valorTotal ?? '';
        setCobrancaFormInitialValues({
          valorTotal: tipo === 'avulsa' ? '' : valorTotal !== null && valorTotal !== undefined ? valorTotal : '',
          numeroPedidoEmbarcador: numeroPedido || '',
          observacoes: buildObservacoesCobranca(calculo),
          tipo: '',
          dataCobranca: ''
        });
        setShowCobrancaModal(true);
      } catch (error) {
        handleCobrancaErro(error, 'Erro ao calcular cobranca');
        console.error('Erro ao calcular cobranca:', error);
      } finally {
        setCobrancaLoading(false);
        setBulkCobrancaLoading(null);
      }
    },
    [selectedNotas, handleCobrancaErro]
  );

  const handleRecalcularCobrancaArmazenagem = useCallback(
    async (dataCobrancaValue) => {
      if (cobrancaTipo !== 'armazenagem') return;
      if (!showCobrancaModal) return;

      const ids = cobrancaNotasSelecionadas.map((item) => item.id).filter(Boolean);
      if (!ids.length) return;

      try {
        setCobrancaRecalculando(true);
        const response = await calcularCobrancaArmazenagem(
          ids,
          dataCobrancaValue ? toApiDateTime(dataCobrancaValue) : null
        );
        const calculo = response?.data || response || null;
        setCobrancaCalculo(calculo);

        const valorTotal = calculo?.valorTotal ?? '';
        cobrancaFormRef.current?.change?.('valorTotal', valorTotal !== null && valorTotal !== undefined ? valorTotal : '');
        cobrancaFormRef.current?.change?.('observacoes', buildObservacoesCobranca(calculo));
      } catch (error) {
        handleCobrancaErro(error, 'Erro ao recalcular cobranca');
        console.error('Erro ao recalcular cobranca:', error);
      } finally {
        setCobrancaRecalculando(false);
      }
    },
    [cobrancaNotasSelecionadas, cobrancaTipo, handleCobrancaErro, showCobrancaModal]
  );

  const handleGerarCobranca = useCallback(async () => {
    if (!cobrancaCalculo) {
      notification.warning('Nenhum calculo disponivel para gerar cobranca.');
      return;
    }

    try {
      setCobrancaGerando(true);
      const ids = cobrancaCalculo.detalhes?.length
        ? cobrancaCalculo.detalhes.map((item) => item.id).filter(Boolean)
        : cobrancaNotasSelecionadas.length
          ? cobrancaNotasSelecionadas.map((item) => item.id).filter(Boolean)
          : cobrancaCalculo.ids?.length
            ? cobrancaCalculo.ids
            : editingId
              ? [editingId]
              : [];
      const formValues = cobrancaFormRef.current?.getState?.()?.values || {};
        const valorTotalInput = parseNumber(formValues.valorTotal);
        const valorTotal = valorTotalInput !== undefined ? valorTotalInput : (cobrancaCalculo.valorTotal ?? 0);
        if (valorTotalInput === undefined) {
          notification.warning('Informe o Valor Total para gerar a cobranca.');
          return;
        }
        const tipoAvulsa = normalizeText(formValues.tipo) || undefined;
        if (cobrancaTipo === 'avulsa' && !tipoAvulsa) {
          notification.warning('Informe o Tipo da cobranca avulsa.');
          return;
        }
        const numeroPedidoEmbarcador = normalizeText(formValues.numeroPedidoEmbarcador) || undefined;
        const observacoes = normalizeText(formValues.observacoes) || undefined;
        const dataCobranca = cobrancaTipo === 'armazenagem'
          ? (formValues.dataCobranca ? toApiDateTime(formValues.dataCobranca) : null)
          : undefined;

      if (!ids.length) {
        notification.warning('Nenhuma nota selecionada para gerar cobranca.');
        return;
      }

        if (cobrancaTipo === 'paletizacao') {
          await registrarCobrancaPaletizacao(ids, valorTotal, numeroPedidoEmbarcador, observacoes);
        } else if (cobrancaTipo === 'etiquetagem') {
          await registrarCobrancaEtiquetagem(ids, valorTotal, numeroPedidoEmbarcador, observacoes);
        } else if (cobrancaTipo === 'avulsa') {
          await registrarCobrancaAvulsa(ids, valorTotal, tipoAvulsa, numeroPedidoEmbarcador, observacoes);
        } else {
          await registrarCobrancaArmazenagem(ids, valorTotal, numeroPedidoEmbarcador, observacoes, dataCobranca);
        }

      notification.success('Cobranca gerada com sucesso!');
      setShowCobrancaModal(false);
      setCobrancaCalculo(null);
      fetchData();
      if (editingId) {
        handleEditNota(editingId);
      }
    } catch (error) {
      notification.error('Erro ao gerar cobranca');
      console.error('Erro ao gerar cobranca:', error);
    } finally {
      setCobrancaGerando(false);
    }
  }, [
    cobrancaCalculo,
    cobrancaTipo,
    cobrancaNotasSelecionadas,
    editingId,
    fetchData,
    handleEditNota
  ]);

  const handleCloseCobrancaModal = useCallback(() => {
    setShowCobrancaModal(false);
    setCobrancaCalculo(null);
    setCobrancaRecalculando(false);
    setCobrancaFormInitialValues({ valorTotal: '', numeroPedidoEmbarcador: '', observacoes: '', tipo: '', dataCobranca: '' });
    setCobrancaNotasSelecionadas([]);
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      const payload = values.id
        ? buildUpdatePayload(formInitialValues, values)
        : buildPayload(values, false);
      if (Object.keys(payload).length === 0) {
        notification.warning('Nenhuma alteracao para salvar.');
        return;
      }

      try {
        setSaving(true);
        let response;

        if (values.id) {
          response = await updateDepositoNotaFiscal(values.id, payload);
          notification.success('Nota de deposito atualizada com sucesso!');
        } else {
          response = await createDepositoNotaFiscal(payload);
          notification.success('Nota de deposito criada com sucesso!');
        }

        const data = response?.data || response || {};
        const mapped = mapApiToFormValues(data);
        setEditingId(data.id || values.id || null);
        syncLookupRefsFromMappedForm(mapped);
        setFormInitialValues(mapped);
        setIsFormCollapsed(false);
        fetchData();
      } catch (error) {
        notification.error('Erro ao salvar nota de deposito');
        console.error('Erro ao salvar nota de deposito:', error);
      } finally {
        setSaving(false);
      }
    },
    [fetchData, formInitialValues, syncLookupRefsFromMappedForm]
  );

  const formKey = useMemo(() => (editingId ? `edit-${editingId}` : 'create'), [editingId]);

  const detalhesColumns = useMemo(() => {
    if (cobrancaTipo === 'paletizacao') {
      return [
        {
          id: 'numeroNF',
          header: 'Numero NF',
          accessorKey: 'numeroNF',
          cell: ({ getValue }) => <span>{getValue() || '-'}</span>
        },
        colunaObsTabelaPreco,
        {
          id: 'quantidadeTotalPaletes',
          header: 'Qtde Paletes',
          accessorKey: 'quantidadeTotalPaletes',
          cell: ({ row }) => <span>{row.original.quantidadePaletesCobranca ?? row.original.quantidadeTotalPaletes ?? '-'}</span>
        },
        {
          id: 'valorUnPallet',
          header: 'Valor Pallet',
          accessorKey: 'valorUnPallet',
          cell: ({ getValue }) => {
            const value = getValue();
            return value === null || value === undefined ? '-' : formatarValorBRL(value);
          }
        },
        {
          id: 'valorTotalCalculado',
          header: 'Total',
          accessorKey: 'valorTotalCalculado',
          cell: ({ getValue }) => {
            const value = getValue();
            return value === null || value === undefined ? '-' : formatarValorBRL(value);
          }
        }
      ];
    }

    if (cobrancaTipo === 'etiquetagem') {
      return [
        {
          id: 'numeroNF',
          header: 'Numero NF',
          accessorKey: 'numeroNF',
          cell: ({ getValue }) => <span>{getValue() || '-'}</span>
        },
        colunaObsTabelaPreco,
        {
          id: 'quantidadeTotalEtiquetas',
          header: 'Qtde Etiquetas',
          accessorKey: 'quantidadeTotalEtiquetas',
          cell: ({ row }) => <span>{row.original.quantidadeEtiquetasCobranca ?? row.original.quantidadeTotalEtiquetas ?? '-'}</span>
        },
        {
          id: 'valorUnEtiqueta',
          header: 'Valor Etiqueta',
          accessorKey: 'valorUnEtiqueta',
          cell: ({ getValue }) => {
            const value = getValue();
            return value === null || value === undefined ? '-' : formatarValorBRL(value);
          }
        },
        {
          id: 'valorTotalCalculado',
          header: 'Total',
          accessorKey: 'valorTotalCalculado',
          cell: ({ getValue }) => {
            const value = getValue();
            return value === null || value === undefined ? '-' : formatarValorBRL(value);
          }
        }
      ];
    }

    if (cobrancaTipo === 'avulsa') {
      return [
        {
          id: 'numeroNF',
          header: 'Numero NF',
          accessorKey: 'numeroNF',
          cell: ({ getValue }) => <span>{getValue() || '-'}</span>
        },
        colunaObsTabelaPreco,
        {
          id: 'quantidadeTotalCaixas',
          header: 'Qtde Caixas',
          accessorKey: 'quantidadeTotalCaixas',
          cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
        },
        {
          id: 'quantidadeTotalPaletes',
          header: 'Qtde Paletes',
          accessorKey: 'quantidadeTotalPaletes',
          cell: ({ row }) => <span>{row.original.quantidadePaletesCobranca ?? row.original.quantidadeTotalPaletes ?? '-'}</span>
        },
        {
          id: 'quantidadeTotalEtiquetas',
          header: 'Qtde Etiquetas',
          accessorKey: 'quantidadeTotalEtiquetas',
          cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
        }
      ];
    }

    return [
      {
        id: 'numeroNF',
        header: 'Numero NF',
        accessorKey: 'numeroNF',
        cell: ({ getValue }) => <span>{getValue() || '-'}</span>
      },
      colunaObsTabelaPreco,
      {
        id: 'dataInicioCobranca',
        header: 'Data Inicio',
        accessorKey: 'dataInicioCobranca',
        cell: ({ getValue }) => (getValue() ? formatarDataSimples(getValue()) : '-')
      },
      {
        id: 'dataFimCobranca',
        header: 'Data Fim',
        accessorKey: 'dataFimCobranca',
        cell: ({ getValue }) => (getValue() ? formatarDataSimples(getValue()) : '-')
      },
      {
        id: 'diasCobrados',
        header: 'Dias',
        accessorKey: 'diasCobrados',
        cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
      },
      {
        id: 'quantidadeTotalPaletes',
        header: 'Qtde Paletes',
        accessorKey: 'quantidadeTotalPaletes',
        cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
      },
      {
        id: 'valorUnArmazenagemPallet',
        header: 'Valor Dia',
        accessorKey: 'valorUnArmazenagemPallet',
        cell: ({ getValue }) => {
          const value = getValue();
          return value === null || value === undefined ? '-' : formatarValorBRL(value);
        }
      },
      {
        id: 'valorTotalCalculado',
        header: 'Total',
        accessorKey: 'valorTotalCalculado',
        cell: ({ getValue }) => {
          const value = getValue();
          return value === null || value === undefined ? '-' : formatarValorBRL(value);
        }
      }
    ];
  }, [cobrancaTipo]);

  const detalhesData = cobrancaCalculo?.detalhes || [];
  const hasCobrancaPendente = cobrancaTipo === 'avulsa'
    ? !!(cobrancaCalculo?.ids && cobrancaCalculo.ids.length > 0)
    : !!(cobrancaCalculo?.nfsCalculadasParaCobranca && cobrancaCalculo.nfsCalculadasParaCobranca.length > 0);
  const auditoriaColumns = useMemo(
    () => [
      {
        id: 'dataHora',
        header: 'Data/Hora',
        accessorKey: 'dataHora',
        size: 180,
        enableSorting: false,
        cell: ({ getValue }) => <span>{formatarDataHoraAuditoria(getValue())}</span>
      },
      {
        id: 'usuario',
        header: 'Usuario',
        accessorKey: 'usuario',
        size: 160,
        enableSorting: false,
        cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={22} />
      },
      {
        id: 'acao',
        header: 'Acao',
        accessorKey: 'acao',
        size: 130,
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue();
          const variant = value === 'Criacao'
            ? 'success'
            : value === 'Atualizacao'
              ? 'warning'
              : value === 'Exclusao'
                ? 'danger'
                : 'default';
          return <TableBadge label={value || '-'} variant={variant} />;
        }
      },
      {
        id: 'campo',
        header: 'Campo',
        accessorKey: 'campo',
        size: 180,
        enableSorting: false,
        cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={24} />
      },
      {
        id: 'valorAntigo',
        header: 'Valor Antigo',
        accessorKey: 'valorAntigo',
        size: 220,
        enableSorting: false,
        cell: ({ getValue }) => <TruncatedText value={getValue() ?? '-'} maxLength={30} />
      },
      {
        id: 'valorNovo',
        header: 'Valor Novo',
        accessorKey: 'valorNovo',
        size: 220,
        enableSorting: false,
        cell: ({ getValue }) => <TruncatedText value={getValue() ?? '-'} maxLength={30} />
      }
    ],
    []
  );
  const auditoriaGeralColumns = useMemo(
    () => [
      {
        id: 'numeroNF',
        header: 'Numero NF',
        accessorKey: 'numeroNF',
        size: 140,
        enableSorting: false,
        cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={16} />
      },
      {
        id: 'cnpjCliente',
        header: 'CNPJ Cliente',
        accessorKey: 'cnpjCliente',
        size: 160,
        enableSorting: false,
        cell: ({ getValue }) => <span>{formatarCNPJ(getValue() || '') || '-'}</span>
      },
      ...auditoriaColumns
    ],
    [auditoriaColumns]
  );
  const auditoriaTabelaPrecoColumns = useMemo(
    () => [
      {
        id: 'nomeTabela',
        header: 'Nome',
        accessorKey: 'nomeTabela',
        size: 180,
        enableSorting: false,
        cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={24} />
      },
      {
        id: 'responsavel',
        header: 'Responsavel',
        accessorKey: 'responsavel',
        size: 170,
        enableSorting: false,
        cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={22} />
      },
      {
        id: 'cnpjCliente',
        header: 'CNPJ Cliente',
        accessorKey: 'cnpjCliente',
        size: 160,
        enableSorting: false,
        cell: ({ getValue }) => <span>{formatarCNPJ(getValue() || '') || '-'}</span>
      },
      {
        id: 'nomeCliente',
        header: 'Nome Cliente',
        accessorKey: 'nomeCliente',
        size: 180,
        enableSorting: false,
        cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={24} />
      },
      ...auditoriaColumns
    ],
    [auditoriaColumns]
  );

  return (
    <>
    <Card>
      <CardBody>
          <DepositoNotasHeader
            totalRegistros={totalRegistros}
            onAdicionar={handleAdicionar}
            onExportarPendencias={handleExportarPendencias}
            exportarPendenciasLoading={exportandoPendencias}
            onMostrarDocumentacao={handleMostrarDocumentacao}
            documentacaoLoading={documentacaoLoading}
            onMostrarAuditoriaGeral={handleAbrirAuditoriaGeralModal}
            auditoriaGeralLoading={auditoriaGeralLoading}
            onMostrarAuditoriaTabelaPreco={handleAbrirAuditoriaTabelaPrecoModal}
            auditoriaTabelaPrecoLoading={auditoriaTabelaPrecoLoading}
            onImportar={handleImportar}
            onClientes={handleClientes}
            clientesLoading={false}
            onGerarCobrancaPaletizacao={() => handleNovaCobrancaEmLote('paletizacao')}
            onGerarCobrancaEtiquetagem={() => handleNovaCobrancaEmLote('etiquetagem')}
            onGerarCobrancaArmazenagem={() => handleNovaCobrancaEmLote('armazenagem')}
            onGerarCobrancaAvulsa={() => handleNovaCobrancaEmLote('avulsa')}
            bulkCobrancaLoading={bulkCobrancaLoading}
            onArmazenamentoLote={handleAbrirArmazenamentoLoteModal}
            armazenamentoLoteLoading={armazenamentoLoteSalvando || loadingArmazenamentoLookup}
            onAlterarLote={handleAbrirAlteracaoLoteModal}
            alteracaoLoteLoading={alteracaoLoteSalvando}
            onPendenciaChange={handlePendenciaHeaderComResetSelecao}
            pendenciaValue={pendenciaHeader}
            pendenciaCounts={resumoPendencias}
            selectedCount={selectedNotas.length}
        />

        <DepositoNotasFilter
          loading={filterLoading}
          onApply={handleBuscarComResetSelecao}
          onLimpar={handleLimparFiltrosComResetSelecao}
        />

        <DepositoNotasTable
          data={data}
          loading={loading}
          totalRegistros={totalRegistros}
          paginationState={paginationState}
          setPaginationState={handlePaginationChangeComResetSelecao}
          onAtualizar={handleEditNota}
          loadingAtualizarId={loadingEditId}
          onCadastrarDivergencia={abrirDivergenciaModal}
          onEnviarAnexo={abrirAnexoModal}
          onEnviarCanhoto={abrirCanhotoModal}
          onVisualizarCanhoto={handleVisualizarCanhoto}
          onAuditoria={abrirAuditoriaModal}
          onFinalizar={abrirFinalizarModal}
          loadingAcao={loadingAcao}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelectNota}
          onToggleSelectAll={handleToggleSelectAll}
          allPageSelected={allPageSelected}
          selectAllDisabled={selectAllDisabled}
          rowTitleGetter={(row) => row?.pendenciasTexto || undefined}
          inlineEditConfig={inlineEditConfig}
        />


<div ref={formularioRef}>
        <CollapsibleCard
          className="mt-3"
          title="Formulario da Nota de Deposito"
          isCollapsed={isFormCollapsed}
          onToggle={setIsFormCollapsed}
          maxContentHeight="auto"
        >
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={formInitialValues}
            validate={validateDepositoNotaFiscalForm}
          >
            {({ handleSubmit, form }) => {
              formRef.current = form;

              return (
                <FormContainer onSubmit={handleSubmit}>
                  <DynamicForm
                    structure={depositoFormStructureWithLookup}
                    sections={depositoNotaFiscalFormSections()}
                    readOnly={false}
                    modalComponents={modalComponents}
                    modalProps={modalProps}
                  />

                    <FormButtonToolbar className="mt-3 d-flex justify-content-between align-items-center w-100">
                      <div>
                        <Button variant="secondary" type="button" onClick={handleClear} disabled={saving}>
                          Limpar
                        </Button>
                        <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                          Salvar
                        </ButtonWithLoading>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          type="button"
                          disabled={!editingId}
                          onClick={handleAbrirCobrancasModal}
                        >
                          Cobranças
                        </Button>
                        <Button
                          variant="outline-primary"
                          type="button"
                          disabled={!editingId}
                          onClick={handleAbrirAnexosModal}
                        >
                          Anexos
                        </Button>
                      </div>
                    </FormButtonToolbar>
                </FormContainer>
              );
            }}
          </Form>
        </CollapsibleCard>
        </div>
      </CardBody>
    </Card>
    <ImportarDepositoNotasModal
      show={showImportarModal}
      onClose={handleCloseImportar}
      onImportado={fetchData}
    />
    <DepositoClientesModal
      show={showClientesModal}
      onHide={handleCloseClientes}
    />
    <DepositoNotaFiscalAnexosModal
      show={showAnexosListModal}
      onHide={handleCloseAnexosModal}
      notaId={editingId}
      numeroNF={formInitialValues.numeroNF}
      reloadTrigger={anexosReloadTrigger}
    />
    <DepositoNotaFiscalCobrancasModal
      show={showCobrancasListModal}
      onHide={handleCloseCobrancasModal}
      notaId={editingId}
      numeroNF={formInitialValues.numeroNF}
    />
    <Modal show={showCobrancaModal} onHide={handleCloseCobrancaModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Nova Cobranca {getCobrancaTipoLabel(cobrancaTipo)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cobrancaLoading && (
          <div className="text-center py-4">
            <span>Calculando cobranca...</span>
          </div>
        )}

        {!cobrancaLoading && cobrancaCalculo && (
          <>
            {cobrancaCalculo.regraResumo && (
              <p className="text-muted">{cobrancaCalculo.regraResumo}</p>
            )}
            <div className="mb-3">
              <div className="mb-3">
                <div><strong>CNPJ:</strong> {cobrancaCalculo.cnpjCliente || '-'}</div>
                {cobrancaTipo === 'armazenagem' && cobrancaRecalculando && (
                  <div className="text-muted mt-2">Recalculando cobranca...</div>
                )}
              </div>
              <Form onSubmit={() => {}} initialValues={cobrancaFormInitialValues} subscription={{}}>
                {({ form, handleSubmit }) => {
                  cobrancaFormRef.current = form;

                  return (
                    <FormContainer
                      onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit(event);
                      }}
                    >
                      <DynamicForm
                        structure={cobrancaFormStructure}
                        sections={cobrancaFormSections}
                        showSectionHeaders={false}
                        readOnly={false}
                      />
                      <CobrancaArmazenagemDataObserver
                        enabled={cobrancaTipo === 'armazenagem'}
                        onChange={handleRecalcularCobrancaArmazenagem}
                      />
                    </FormContainer>
                  );
                }}
              </Form>
              <div>
                <strong>{cobrancaTipo === 'avulsa' ? 'NFs Selecionadas:' : 'NFs Calculadas:'}</strong>{' '}
                {cobrancaCalculo.nfsCalculadasParaCobranca?.length
                  ? cobrancaCalculo.nfsCalculadasParaCobranca.join(', ')
                  : '-'}
              </div>
              {cobrancaTipo !== 'avulsa' && (
                <div>
                  <strong>NFs Ja Cobradas:</strong>{' '}
                  {cobrancaCalculo.nfsJaCobradas?.length
                    ? cobrancaCalculo.nfsJaCobradas.join(', ')
                    : '-'}
                </div>
              )}
            </div>

            {!hasCobrancaPendente && cobrancaTipo !== 'avulsa' && (
              <div className="alert alert-info">
                Nenhuma cobranca pendente para esta nota fiscal.
              </div>
            )}

            {detalhesData.length > 0 && (
              <TanStackTable
                columns={detalhesColumns}
                data={detalhesData}
                loading={false}
                withPagination={false}
                enableRowColors={true}
                className="mt-2"
              />
            )}

            {cobrancaCalculo?.observacoesEmissao && (
              <div className="mt-3">
                <strong>Observacoes Emissao:</strong>
                <p className="mb-0 text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                  {cobrancaCalculo.observacoesEmissao}
                </p>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseCobrancaModal}>
          Fechar
        </Button>
        <ButtonWithLoading
          variant="primary"
          loading={cobrancaGerando}
          onClick={handleGerarCobranca}
          disabled={!cobrancaCalculo || cobrancaLoading}
        >
          Gerar Cobranca
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
    <Modal show={showCobrancaErroModal} onHide={handleCloseCobrancaErroModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Erros ao calcular cobranca {getCobrancaTipoLabel(cobrancaTipo)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cobrancaErros.length === 0 ? (
          <div className="text-muted">Nenhum detalhe de erro retornado.</div>
        ) : (
          <ul className="mb-0">
            {cobrancaErros.map((erro, index) => (
              <li key={`cobranca-erro-${index}`}>{erro}</li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseCobrancaErroModal}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showDivergenciaModal} onHide={handleCloseDivergenciaModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Cadastrar Divergencia {formInitialValues.numeroNF ? `- NF ${formInitialValues.numeroNF}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted mb-3">
          <div><strong>CNPJ:</strong> {formInitialValues.cnpjCliente || '-'}</div>
          <div><strong>Serie NF:</strong> {formInitialValues.serieNF || '-'}</div>
        </div>
        <RBForm.Group className="mb-3">
          <RBForm.Label>Mensagem</RBForm.Label>
          <RBForm.Control
            as="textarea"
            rows={4}
            value={divergenciaMensagem}
            onChange={(event) => setDivergenciaMensagem(event.target.value)}
            placeholder="Descreva a divergencia"
          />
        </RBForm.Group>
        <RBForm.Group>
          <RBForm.Label>Anexos</RBForm.Label>
          <RBForm.Control
            type="file"
            multiple
            onChange={(event) => setDivergenciaArquivos(Array.from(event.target.files || []))}
          />
        </RBForm.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseDivergenciaModal} disabled={divergenciaSalvando}>
          Cancelar
        </Button>
        <ButtonWithLoading variant="primary" loading={divergenciaSalvando} onClick={handleSalvarDivergencia}>
          Salvar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>

    <Modal show={showAnexoModal} onHide={handleCloseAnexoModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Enviar Anexos {formInitialValues.numeroNF ? `- NF ${formInitialValues.numeroNF}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted mb-3">
          <div><strong>CNPJ:</strong> {formInitialValues.cnpjCliente || '-'}</div>
          <div><strong>Serie NF:</strong> {formInitialValues.serieNF || '-'}</div>
        </div>
        <RBForm.Group>
          <RBForm.Label>Anexos</RBForm.Label>
          <RBForm.Control
            type="file"
            multiple
            onChange={(event) => setAnexoArquivos(Array.from(event.target.files || []))}
          />
        </RBForm.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseAnexoModal} disabled={anexoSalvando}>
          Cancelar
        </Button>
        <ButtonWithLoading variant="primary" loading={anexoSalvando} onClick={handleSalvarAnexos}>
          Salvar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>

    <Modal show={showCanhotoModal} onHide={handleCloseCanhotoModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Enviar Canhoto {formInitialValues.numeroNF ? `- NF ${formInitialValues.numeroNF}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted mb-3">
          <div><strong>CNPJ:</strong> {formInitialValues.cnpjCliente || '-'}</div>
        </div>
        <RBForm.Group className="mb-3">
          <RBForm.Label>Numero de Serie NF</RBForm.Label>
          <RBForm.Control
            type="text"
            value={canhotoSerieNF}
            onChange={(event) => setCanhotoSerieNF(event.target.value)}
            placeholder="Informe a serie da NF"
          />
        </RBForm.Group>
        <RBForm.Group>
          <RBForm.Label>Imagem do Canhoto</RBForm.Label>
          <RBForm.Control
            type="file"
            accept="image/*"
            onChange={(event) => setCanhotoArquivo(event.target.files?.[0] || null)}
          />
        </RBForm.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseCanhotoModal} disabled={canhotoSalvando}>
          Cancelar
        </Button>
        <ButtonWithLoading variant="primary" loading={canhotoSalvando} onClick={handleSalvarCanhoto}>
          Salvar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>

    <Modal show={showVisualizarCanhotoModal} onHide={handleCloseVisualizarCanhotoModal} animation={false} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Visualizar Canhoto {canhotoPreviewTitulo ? `- ${canhotoPreviewTitulo}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {canhotoPreviewSrc ? (
          <img
            src={canhotoPreviewSrc}
            alt="Canhoto"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseVisualizarCanhotoModal}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal show={showAuditoriaGeralModal} onHide={handleCloseAuditoriaGeralModal} animation={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Auditoria Geral do Deposito</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FilterPanel
          filters={auditoriaGeralFilterConfig}
          onApply={handleAuditoriaGeralFiltros}
          title="Filtros de Auditoria"
          loading={auditoriaGeralLoading}
          defaultCollapsed={false}
        />

        {auditoriaGeralData.length === 0 && !auditoriaGeralLoading ? (
          <div className="text-muted mt-3">Nenhum registro de auditoria encontrado.</div>
        ) : (
          <TanStackTable
            key={`deposito-auditoria-geral-${auditoriaGeralPagination.pageIndex}-${auditoriaGeralPagination.pageSize}`}
            columns={auditoriaGeralColumns}
            data={auditoriaGeralData}
            loading={auditoriaGeralLoading}
            withPagination={true}
            manualPagination={true}
            totalItems={auditoriaGeralTotal}
            controlledPagination={auditoriaGeralPagination}
            onPaginationChange={handleAuditoriaGeralPagination}
            pageSizeOptions={[10, 25, 50]}
            enableRowColors={true}
            className="mt-3"
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseAuditoriaGeralModal}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal show={showAuditoriaTabelaPrecoModal} onHide={handleCloseAuditoriaTabelaPrecoModal} animation={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Auditoria de Tabela de Preco</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FilterPanel
          filters={auditoriaTabelaPrecoFilterConfig}
          onApply={handleAuditoriaTabelaPrecoFiltros}
          title="Filtros de Auditoria"
          loading={auditoriaTabelaPrecoLoading}
          defaultCollapsed={false}
        />

        {auditoriaTabelaPrecoData.length === 0 && !auditoriaTabelaPrecoLoading ? (
          <div className="text-muted mt-3">Nenhum registro de auditoria encontrado.</div>
        ) : (
          <TanStackTable
            key={`deposito-auditoria-tabela-preco-${auditoriaTabelaPrecoPagination.pageIndex}-${auditoriaTabelaPrecoPagination.pageSize}`}
            columns={auditoriaTabelaPrecoColumns}
            data={auditoriaTabelaPrecoData}
            loading={auditoriaTabelaPrecoLoading}
            withPagination={true}
            manualPagination={true}
            totalItems={auditoriaTabelaPrecoTotal}
            controlledPagination={auditoriaTabelaPrecoPagination}
            onPaginationChange={handleAuditoriaTabelaPrecoPagination}
            pageSizeOptions={[10, 25, 50]}
            enableRowColors={true}
            className="mt-3"
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseAuditoriaTabelaPrecoModal}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal show={showAuditoriaModal} onHide={handleCloseAuditoriaModal} animation={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Auditoria da Nota Fiscal {formInitialValues.numeroNF ? `- NF ${formInitialValues.numeroNF}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted mb-3">
          <div><strong>CNPJ:</strong> {formInitialValues.cnpjCliente || '-'}</div>
          <div><strong>Nome Cliente:</strong> {formInitialValues.nomeCliente || '-'}</div>
        </div>

        {auditoriaData.length === 0 && !auditoriaLoading ? (
          <div className="text-muted">Nenhum registro de auditoria encontrado para esta nota.</div>
        ) : (
          <TanStackTable
            columns={auditoriaColumns}
            data={auditoriaData}
            loading={auditoriaLoading}
            withPagination={true}
            manualPagination={true}
            totalItems={auditoriaTotal}
            controlledPagination={auditoriaPagination}
            onPaginationChange={handleAuditoriaPagination}
            pageSizeOptions={[10, 25, 50]}
            enableRowColors={true}
            className="mt-2"
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseAuditoriaModal}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal show={showFinalizarModal} onHide={handleCloseFinalizarModal} animation={false} size="md">
      <Modal.Header closeButton>
        <Modal.Title>Finalizar Nota Fiscal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Deseja finalizar a nota fiscal {formInitialValues.numeroNF ? `NF ${formInitialValues.numeroNF}` : ''}?
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseFinalizarModal} disabled={finalizarSalvando}>
          Cancelar
        </Button>
        <ButtonWithLoading variant="success" loading={finalizarSalvando} onClick={handleFinalizarNota}>
          Confirmar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
    <Modal show={showFinalizarLoteModal} onHide={handleCloseFinalizarLoteModal} animation={false} size="md">
      <Modal.Header closeButton>
        <Modal.Title>Finalizar Notas Fiscais</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Deseja finalizar em lote {selectedNotas.length} nota(s) fiscal(is)?
        </p>
        {previewNotasFinalizacaoLote.length > 0 && (
          <ul className="mb-0">
            {previewNotasFinalizacaoLote.map((nota) => (
              <li key={`finalizar-lote-preview-${nota.id}`}>
                NF {nota.numeroNF || nota.id}
              </li>
            ))}
            {selectedNotas.length > previewNotasFinalizacaoLote.length && (
              <li>... e mais {selectedNotas.length - previewNotasFinalizacaoLote.length} nota(s)</li>
            )}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseFinalizarLoteModal} disabled={finalizarLoteSalvando}>
          Cancelar
        </Button>
        <ButtonWithLoading variant="success" loading={finalizarLoteSalvando} onClick={handleFinalizarNotasEmLote}>
          Confirmar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
    <Modal show={showFinalizarLoteResumoModal} onHide={handleCloseFinalizarLoteResumoModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Resumo da Finalizacao em Lote</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">Quantidade solicitada: {finalizacaoLoteResumo?.quantidadeSolicitada ?? 0}</p>
        <p className="mb-2">Quantidade processada: {finalizacaoLoteResumo?.quantidadeProcessada ?? 0}</p>
        <p className="mb-2 text-success">Quantidade com sucesso: {finalizacaoLoteResumo?.quantidadeSucesso ?? 0}</p>
        <p className="mb-3 text-danger">Quantidade com erro: {finalizacaoLoteResumo?.quantidadeErro ?? 0}</p>

        {resumoFinalizacaoLoteErros.length === 0 ? (
          <div className="text-muted">Nenhum erro retornado na finalizacao em lote.</div>
        ) : (
          <ul className="mb-0">
            {resumoFinalizacaoLoteErros.map((item) => (
              <li key={`finalizacao-lote-erro-${item.id}`}>
                {item.numeroNF ? `NF ${item.numeroNF}` : `Id ${item.id}`}: {item.mensagem || 'Erro nao informado.'}
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseFinalizarLoteResumoModal}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showArmazenamentoLoteModal} onHide={handleCloseArmazenamentoLoteModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Armazenamento em Lote</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Deseja mover {selectedNotas.length} nota(s) fiscal(is) para um enderecamento?
        </p>
        {previewNotasArmazenamentoLote.length > 0 && (
          <ul className="mb-3">
            {previewNotasArmazenamentoLote.map((nota) => (
              <li key={`armazenamento-lote-preview-${nota.id}`}>
                NF {nota.numeroNF || nota.id}
              </li>
            ))}
            {selectedNotas.length > previewNotasArmazenamentoLote.length && (
              <li>... e mais {selectedNotas.length - previewNotasArmazenamentoLote.length} nota(s)</li>
            )}
          </ul>
        )}

        <div className="border rounded p-3">
          <div className="row">
            <div className="col-md-4 mb-3">
              <RBForm.Label>Armazem</RBForm.Label>
              <RBForm.Select
                value={armazenamentoLoteForm.idDepositoArmazem}
                onChange={handleArmazenamentoLoteFieldChange('idDepositoArmazem')}
                disabled={loadingArmazenamentoLookup || armazenamentoLoteSalvando}
              >
                <option value="">Selecione...</option>
                {armazemDepositoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </RBForm.Select>
            </div>
            <div className="col-md-4 mb-3">
              <SearchSelect
                label="Endereco"
                placeholder="Digite para buscar..."
                options={enderecoDepositoOptions}
                onSearch={handleBuscarEnderecoDeposito}
                minSearchLength={1}
                maxResults={50}
                value={armazenamentoLoteForm.idDepositoEndereco}
                onChange={handleArmazenamentoLoteEnderecoChange}
                disabled={!armazenamentoLoteForm.idDepositoArmazem || armazenamentoLoteSalvando}
              />
            </div>
            <div className="col-md-4 mb-3">
              <RBForm.Label>Local Armazenamento</RBForm.Label>
              <RBForm.Select
                value={armazenamentoLoteForm.idDepositoArmazenagemLocal}
                onChange={handleArmazenamentoLoteFieldChange('idDepositoArmazenagemLocal')}
                disabled={loadingArmazenamentoLookup || armazenamentoLoteSalvando}
              >
                <option value="">Selecione...</option>
                {armazenagemLocalOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </RBForm.Select>
            </div>
          </div>
          <small className="text-muted">
            O armazem filtra os enderecos disponiveis. O local de armazenamento sera aplicado a todas as notas selecionadas.
          </small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseArmazenamentoLoteModal} disabled={armazenamentoLoteSalvando}>
          Cancelar
        </Button>
        <ButtonWithLoading variant="info" loading={armazenamentoLoteSalvando} onClick={handleSalvarArmazenamentoLote}>
          Confirmar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
    <Modal show={showArmazenamentoLoteResumoModal} onHide={handleCloseArmazenamentoLoteResumoModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Resumo do Armazenamento em Lote</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">Quantidade solicitada: {armazenamentoLoteResumo?.quantidadeSolicitada ?? 0}</p>
        <p className="mb-2">Quantidade processada: {armazenamentoLoteResumo?.quantidadeProcessada ?? 0}</p>
        <p className="mb-2 text-success">Quantidade com sucesso: {armazenamentoLoteResumo?.quantidadeSucesso ?? 0}</p>
        <p className="mb-3 text-danger">Quantidade com erro: {armazenamentoLoteResumo?.quantidadeErro ?? 0}</p>

        {resumoArmazenamentoLoteErros.length === 0 ? (
          <div className="text-muted">Nenhum erro retornado no armazenamento em lote.</div>
        ) : (
          <ul className="mb-0">
            {resumoArmazenamentoLoteErros.map((item) => (
              <li key={`armazenamento-lote-erro-${item.id}`}>
                {item.numeroNF ? `NF ${item.numeroNF}` : `Id ${item.id}`}: {item.mensagem || 'Erro nao informado.'}
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseArmazenamentoLoteResumoModal}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showAlteracaoLoteModal} onHide={handleCloseAlteracaoLoteModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Alterar Notas Fiscais em Lote</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Deseja alterar em lote {selectedNotas.length} nota(s) fiscal(is)?
        </p>
        {previewNotasAlteracaoLote.length > 0 && (
          <ul className="mb-3">
            {previewNotasAlteracaoLote.map((nota) => (
              <li key={`alteracao-lote-preview-${nota.id}`}>
                NF {nota.numeroNF || nota.id}
              </li>
            ))}
            {selectedNotas.length > previewNotasAlteracaoLote.length && (
              <li>... e mais {selectedNotas.length - previewNotasAlteracaoLote.length} nota(s)</li>
            )}
          </ul>
        )}

        <div className="border rounded p-3">
          <div className="row">
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-finalizado-aplicar"
                label="Aplicar Finalizado"
                checked={alteracaoLoteForm.aplicarFinalizado}
                onChange={handleAlteracaoLoteAplicarChange('aplicarFinalizado')}
              />
              <RBForm.Check
                className="mt-2"
                id="alteracao-lote-finalizado"
                label="Finalizado"
                checked={alteracaoLoteForm.finalizado}
                onChange={handleAlteracaoLoteFieldChange('finalizado')}
                disabled={!alteracaoLoteForm.aplicarFinalizado}
              />
            </div>
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-divergencia-aplicar"
                label="Aplicar Existe Divergencia"
                checked={alteracaoLoteForm.aplicarExisteDivergencia}
                onChange={handleAlteracaoLoteAplicarChange('aplicarExisteDivergencia')}
              />
              <RBForm.Check
                className="mt-2"
                id="alteracao-lote-divergencia"
                label="Existe Divergencia"
                checked={alteracaoLoteForm.existeDivergencia}
                onChange={handleAlteracaoLoteFieldChange('existeDivergencia')}
                disabled={!alteracaoLoteForm.aplicarExisteDivergencia}
              />
            </div>
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-data-saida-aplicar"
                label="Aplicar Data Saida"
                checked={alteracaoLoteForm.aplicarDataSaida}
                onChange={handleAlteracaoLoteAplicarChange('aplicarDataSaida')}
              />
              <RBForm.Control
                className="mt-2"
                type="datetime-local"
                value={alteracaoLoteForm.dataSaida}
                onChange={handleAlteracaoLoteFieldChange('dataSaida')}
                disabled={!alteracaoLoteForm.aplicarDataSaida}
              />
            </div>
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-data-agendamento-aplicar"
                label="Aplicar Data Agendamento"
                checked={alteracaoLoteForm.aplicarDataAgendamento}
                onChange={handleAlteracaoLoteAplicarChange('aplicarDataAgendamento')}
              />
              <RBForm.Control
                className="mt-2"
                type="datetime-local"
                value={alteracaoLoteForm.dataAgendamento}
                onChange={handleAlteracaoLoteFieldChange('dataAgendamento')}
                disabled={!alteracaoLoteForm.aplicarDataAgendamento}
              />
            </div>
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-data-chegada-aplicar"
                label="Aplicar Data Chegada"
                checked={alteracaoLoteForm.aplicarDataChegada}
                onChange={handleAlteracaoLoteAplicarChange('aplicarDataChegada')}
              />
              <RBForm.Control
                className="mt-2"
                type="datetime-local"
                value={alteracaoLoteForm.dataChegada}
                onChange={handleAlteracaoLoteFieldChange('dataChegada')}
                disabled={!alteracaoLoteForm.aplicarDataChegada}
              />
            </div>
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-data-leitura-aplicar"
                label="Aplicar Data Leitura"
                checked={alteracaoLoteForm.aplicarDataLeitura}
                onChange={handleAlteracaoLoteAplicarChange('aplicarDataLeitura')}
              />
              <RBForm.Control
                className="mt-2"
                type="datetime-local"
                value={alteracaoLoteForm.dataLeitura}
                onChange={handleAlteracaoLoteFieldChange('dataLeitura')}
                disabled={!alteracaoLoteForm.aplicarDataLeitura}
              />
            </div>
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-data-conferencia-aplicar"
                label="Aplicar Data Conferencia"
                checked={alteracaoLoteForm.aplicarDataConferencia}
                onChange={handleAlteracaoLoteAplicarChange('aplicarDataConferencia')}
              />
              <RBForm.Control
                className="mt-2"
                type="datetime-local"
                value={alteracaoLoteForm.dataConferencia}
                onChange={handleAlteracaoLoteFieldChange('dataConferencia')}
                disabled={!alteracaoLoteForm.aplicarDataConferencia}
              />
            </div>
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-data-armazenagem-aplicar"
                label="Aplicar Data Armazenagem"
                checked={alteracaoLoteForm.aplicarDataArmazenagem}
                onChange={handleAlteracaoLoteAplicarChange('aplicarDataArmazenagem')}
              />
              <RBForm.Control
                className="mt-2"
                type="datetime-local"
                value={alteracaoLoteForm.dataArmazenagem}
                onChange={handleAlteracaoLoteFieldChange('dataArmazenagem')}
                disabled={!alteracaoLoteForm.aplicarDataArmazenagem}
              />
            </div>
            <div className="col-md-6 mb-3">
              <RBForm.Check
                id="alteracao-lote-tipo-operacao-aplicar"
                label="Aplicar Tipo Operacao"
                checked={alteracaoLoteForm.aplicarTipoOperacao}
                onChange={handleAlteracaoLoteAplicarChange('aplicarTipoOperacao')}
              />
              <RBForm.Control
                className="mt-2"
                type="text"
                value={alteracaoLoteForm.tipoOperacao}
                onChange={handleAlteracaoLoteFieldChange('tipoOperacao')}
                disabled={!alteracaoLoteForm.aplicarTipoOperacao}
                placeholder="Informe o tipo de operacao"
              />
            </div>
            <div className="col-12 mb-3">
              <RBForm.Check
                id="alteracao-lote-observacoes-aplicar"
                label="Aplicar Observacoes"
                checked={alteracaoLoteForm.aplicarObservacoes}
                onChange={handleAlteracaoLoteAplicarChange('aplicarObservacoes')}
              />
              <RBForm.Control
                className="mt-2"
                as="textarea"
                rows={4}
                value={alteracaoLoteForm.observacoes}
                onChange={handleAlteracaoLoteFieldChange('observacoes')}
                disabled={!alteracaoLoteForm.aplicarObservacoes}
                placeholder="Informe as observacoes"
              />
            </div>
            <div className="col-md-4 mb-3">
              <RBForm.Check
                id="alteracao-lote-cobranca-paletizacao-aplicar"
                label="Aplicar Cobranca Paletizacao"
                checked={alteracaoLoteForm.aplicarCobrancaPaletizacaoRealizada}
                onChange={handleAlteracaoLoteAplicarChange('aplicarCobrancaPaletizacaoRealizada')}
              />
              <RBForm.Check
                className="mt-2"
                id="alteracao-lote-cobranca-paletizacao"
                label="Cobranca Paletizacao"
                checked={alteracaoLoteForm.cobrancaPaletizacaoRealizada}
                onChange={handleAlteracaoLoteFieldChange('cobrancaPaletizacaoRealizada')}
                disabled={!alteracaoLoteForm.aplicarCobrancaPaletizacaoRealizada}
              />
            </div>
            <div className="col-md-4 mb-3">
              <RBForm.Check
                id="alteracao-lote-cobranca-etiquetagem-aplicar"
                label="Aplicar Cobranca Etiquetagem"
                checked={alteracaoLoteForm.aplicarCobrancaEtiquetagemRealizada}
                onChange={handleAlteracaoLoteAplicarChange('aplicarCobrancaEtiquetagemRealizada')}
              />
              <RBForm.Check
                className="mt-2"
                id="alteracao-lote-cobranca-etiquetagem"
                label="Cobranca Etiquetagem"
                checked={alteracaoLoteForm.cobrancaEtiquetagemRealizada}
                onChange={handleAlteracaoLoteFieldChange('cobrancaEtiquetagemRealizada')}
                disabled={!alteracaoLoteForm.aplicarCobrancaEtiquetagemRealizada}
              />
            </div>
            <div className="col-md-4 mb-3">
              <RBForm.Check
                id="alteracao-lote-cobranca-armazenagem-aplicar"
                label="Aplicar Cobranca Armazenagem"
                checked={alteracaoLoteForm.aplicarCobrancaArmazenagemRealizada}
                onChange={handleAlteracaoLoteAplicarChange('aplicarCobrancaArmazenagemRealizada')}
              />
              <RBForm.Check
                className="mt-2"
                id="alteracao-lote-cobranca-armazenagem"
                label="Cobranca Armazenagem"
                checked={alteracaoLoteForm.cobrancaArmazenagemRealizada}
                onChange={handleAlteracaoLoteFieldChange('cobrancaArmazenagemRealizada')}
                disabled={!alteracaoLoteForm.aplicarCobrancaArmazenagemRealizada}
              />
            </div>
          </div>
          <small className="text-muted">
            Campos marcados em "Aplicar" com valor vazio serao limpos nas notas selecionadas.
          </small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseAlteracaoLoteModal} disabled={alteracaoLoteSalvando}>
          Cancelar
        </Button>
        <ButtonWithLoading variant="warning" loading={alteracaoLoteSalvando} onClick={handleSalvarAlteracaoLote}>
          Confirmar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
    <Modal show={showAlteracaoLoteResumoModal} onHide={handleCloseAlteracaoLoteResumoModal} animation={false} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Resumo da Alteracao em Lote</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">Quantidade solicitada: {alteracaoLoteResumo?.quantidadeSolicitada ?? 0}</p>
        <p className="mb-2">Quantidade processada: {alteracaoLoteResumo?.quantidadeProcessada ?? 0}</p>
        <p className="mb-2 text-success">Quantidade com sucesso: {alteracaoLoteResumo?.quantidadeSucesso ?? 0}</p>
        <p className="mb-3 text-danger">Quantidade com erro: {alteracaoLoteResumo?.quantidadeErro ?? 0}</p>

        {resumoAlteracaoLoteErros.length === 0 ? (
          <div className="text-muted">Nenhum erro retornado na alteracao em lote.</div>
        ) : (
          <ul className="mb-0">
            {resumoAlteracaoLoteErros.map((item) => (
              <li key={`alteracao-lote-erro-${item.id}`}>
                {item.numeroNF ? `NF ${item.numeroNF}` : `Id ${item.id}`}: {item.mensagem || 'Erro nao informado.'}
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseAlteracaoLoteResumoModal}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default DepositoNotasCard;
